import { useState, useEffect } from 'react'
import { AlertCircle, CheckCircle, ArrowDown, ArrowLeft } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { routerTokens as t } from './RouterDesignSystem'

export interface Project {
  id: string
  title: string
  category: string
  tagline: string
  role: string
  timeline: string
  platform: string
  problem: string
  solution: string
  research: string
  validation: string
  color: string
  draft?: boolean
}

interface Props {
  proj?: Project
}

interface StepItem {
  text: string
  screenTag?: string
}

interface SubBranchData {
  label: string
  screenTag?: string
  sublabel?: string
  steps?: Array<string | StepItem>
  isWarning?: boolean
}

interface FlowBranchData {
  root: string
  screenTag?: string
  action: string
  subBranches: SubBranchData[]
}

export interface KeyScreenData {
  id: string
  num: string
  screenTag?: string
  pathName?: string
  title: string
  caption: string
  isWarning?: boolean
}

export function FlowBranchDiagram({ branch }: { branch: FlowBranchData }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div
        style={{
          fontSize: '0.75rem',
          fontWeight: 800,
          color: '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        操作分支架構 (Flow Branch)
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '1.25rem 1.5rem',
        }}
      >
        {/* Entrance & Action Bar */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.65rem' }}>
          <div
            style={{
              fontSize: '0.9rem',
              fontWeight: 800,
              color: '#0f172a',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {branch.screenTag && (
              <span
                style={{
                  backgroundColor: '#0f172a',
                  color: '#ffffff',
                  fontSize: '0.675rem',
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
              >
                {branch.screenTag}
              </span>
            )}
            <span>{branch.root}</span>
          </div>

          <div style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem' }}>→</div>

          <div
            style={{
              fontSize: '0.9rem',
              fontWeight: 700,
              color: '#334155',
            }}
          >
            {branch.action}
          </div>
        </div>

        {/* Sub-branches Tree */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem',
            paddingLeft: '1.25rem',
            borderLeft: '1.5px dashed #cbd5e1',
            marginLeft: '0.75rem',
          }}
        >
          {branch.subBranches.map((sub, idx) => {
            const isWarn = sub.isWarning
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.65rem',
                  flexWrap: 'wrap',
                  position: 'relative',
                }}
              >
                {/* Horizontal branch line indicator */}
                <div
                  style={{
                    position: 'absolute',
                    left: '-1.25rem',
                    top: '50%',
                    width: '1rem',
                    height: '1.5px',
                    backgroundColor: isWarn ? '#fca5a5' : '#cbd5e1',
                  }}
                />

                <div
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: isWarn ? '#dc2626' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {sub.screenTag && (
                    <span
                      style={{
                        backgroundColor: isWarn ? '#dc2626' : '#0f172a',
                        color: '#ffffff',
                        fontSize: '0.675rem',
                        fontWeight: 800,
                        padding: '1px 5px',
                        borderRadius: '4px',
                      }}
                    >
                      {sub.screenTag}
                    </span>
                  )}
                  {isWarn && <span style={{ fontSize: '0.8rem' }}>⚠️</span>}
                  <span>{sub.label}</span>
                </div>

                {sub.steps && sub.steps.length > 0 && (
                  <>
                    <div style={{ color: isWarn ? '#fca5a5' : '#cbd5e1', fontSize: '0.85rem' }}>
                      →
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {sub.steps.map((st, sIdx) => {
                        const stText = typeof st === 'string' ? st : st.text
                        const stTag = typeof st === 'object' ? st.screenTag : undefined
                        return (
                          <div
                            key={sIdx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: isWarn ? '#991b1b' : '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              {stTag && (
                                <span
                                  style={{
                                    backgroundColor: isWarn ? '#dc2626' : '#0f172a',
                                    color: '#ffffff',
                                    fontSize: '0.675rem',
                                    fontWeight: 800,
                                    padding: '1px 5px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {stTag}
                                </span>
                              )}
                              <span>{stText}</span>
                            </span>
                            {sIdx < sub.steps!.length - 1 && (
                              <span
                                style={{
                                  color: isWarn ? '#fca5a5' : '#cbd5e1',
                                  fontSize: '0.8rem',
                                }}
                              >
                                →
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function InteractiveRouterPrototype({
  defaultTab = 'wifi',
}: {
  defaultTab?: 'wifi' | 'devices' | 'maintenance'
}) {
  const [activeTab, setActiveTab] = useState<'wifi' | 'devices' | 'maintenance'>(defaultTab)

  // ================= Flow 01 (Wi-Fi 設定) State =================
  const [wifiSsid, setWifiSsid] = useState('Chunghwa_Home_5G')
  const [wifiPassword, setWifiPassword] = useState('88888888a')
  const [wifiToast, setWifiToast] = useState<string | null>(null)
  const [showWifiErrorHighlight, setShowWifiErrorHighlight] = useState(false)

  const hasLetter = /[a-zA-Z]/.test(wifiPassword)
  const hasNumber = /[0-9]/.test(wifiPassword)
  const isPasswordValid = wifiPassword.length >= 8 && hasLetter && hasNumber
  const isPasswordError = wifiPassword.length > 0 && !isPasswordValid

  const handleSaveWifi = () => {
    if (!isPasswordValid) {
      setShowWifiErrorHighlight(true)
      return
    }
    setShowWifiErrorHighlight(false)
    setWifiToast('已儲存。家中裝置需要重新輸入新密碼才能連線。')
    setTimeout(() => setWifiToast(null), 3000)
  }

  // ================= Flow 02 (裝置管理) State =================
  const [devicesList, setDevicesList] = useState([
    {
      id: 'd1',
      name: '爸爸的 iPhone',
      mac: 'BC:D1:D3:4F:9A:12',
      type: '5G',
      activity: '影音串流',
      icon: '📱',
      isBlocked: false,
    },
    {
      id: 'd2',
      name: '客廳 Smart TV',
      mac: 'A4:C3:F0:88:12:90',
      type: 'LAN 1',
      activity: '4K 串流播放',
      icon: '📺',
      isBlocked: false,
    },
    {
      id: 'd3',
      name: '媽媽的 iPad',
      mac: 'FE:88:12:34:56:78',
      type: '2.4G',
      activity: '網頁瀏覽',
      icon: '📱',
      isBlocked: false,
    },
    {
      id: 'd4',
      name: '工作用 MacBook',
      mac: '70:A2:B4:90:11:22',
      type: '5G',
      activity: '檔案傳輸',
      icon: '💻',
      isBlocked: false,
    },
  ])

  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [editModalDevice, setEditModalDevice] = useState<(typeof devicesList)[0] | null>(null)
  const [editNameInput, setEditNameInput] = useState('')
  const [blockConfirmDevice, setBlockConfirmDevice] = useState<(typeof devicesList)[0] | null>(null)
  const [deviceToast, setDeviceToast] = useState<string | null>(null)

  const handleSaveDeviceName = () => {
    if (!editModalDevice || !editNameInput.trim()) return
    setDevicesList((prev) =>
      prev.map((item) =>
        item.id === editModalDevice.id ? { ...item, name: editNameInput.trim() } : item
      )
    )
    setEditModalDevice(null)
    setEditNameInput('')
  }

  const handleConfirmBlockDevice = () => {
    if (!blockConfirmDevice) return
    const targetName = blockConfirmDevice.name
    setDevicesList((prev) =>
      prev.map((item) => (item.id === blockConfirmDevice.id ? { ...item, isBlocked: true } : item))
    )
    setBlockConfirmDevice(null)
    setDeviceToast(`已將「${targetName}」加入黑名單。`)
    setTimeout(() => setDeviceToast(null), 3000)
  }

  // ================= Flow 03 (系統維護) State =================
  const [rebootModalOpen, setRebootModalOpen] = useState(false)
  const [factoryModalOpen, setFactoryModalOpen] = useState(false)
  const [factoryChecked, setFactoryChecked] = useState(false)
  const [maintenanceStatus, setMaintenanceStatus] = useState<
    'idle' | 'rebooting' | 'reboot_done' | 'resetting' | 'reset_done'
  >('idle')

  const handleConfirmReboot = () => {
    setRebootModalOpen(false)
    setMaintenanceStatus('rebooting')
    setTimeout(() => setMaintenanceStatus('reboot_done'), 2000)
  }

  const handleConfirmFactoryReset = () => {
    if (!factoryChecked) return
    setFactoryModalOpen(false)
    setFactoryChecked(false)
    setMaintenanceStatus('resetting')
    setTimeout(() => setMaintenanceStatus('reset_done'), 2500)
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '640px',
        margin: '0 auto',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        fontSize: '14px',
        color: '#0f172a',
        fontFamily: 'sans-serif',
      }}
    >
      {/* Simulated Browser Window Frame Header Bar */}
      <div
        style={{
          backgroundColor: '#0f172a',
          color: '#ffffff',
          padding: '0.65rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.65rem',
          borderBottom: '1px solid #1e293b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Mac-style Window Controls */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <span
              style={{
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                backgroundColor: '#ef4444',
              }}
            />
            <span
              style={{
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
              }}
            />
            <span
              style={{
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
              }}
            />
          </div>

          {/* Browser Address Bar Pill */}
          <div
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '6px',
              padding: '3px 10px',
              fontSize: '0.75rem',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 400,
            }}
          >
            <span style={{ fontSize: '10px', color: '#10b981' }}>🔒</span>
            <span>
              192.168.1.1/
              {activeTab === 'wifi'
                ? 'wifi/settings'
                : activeTab === 'devices'
                  ? 'wifi/devices'
                  : 'system/maintenance'}
            </span>
          </div>
        </div>

        {/* Entry Selector Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '0.35rem',
            backgroundColor: '#1e293b',
            padding: '3px',
            borderRadius: '8px',
          }}
        >
          <button
            onClick={() => {
              setActiveTab('wifi')
              setOpenMenuId(null)
            }}
            style={{
              padding: '4px 10px',
              fontSize: '0.775rem',
              fontWeight: 500,
              borderRadius: '6px',
              border: 'none',
              backgroundColor: activeTab === 'wifi' ? '#334155' : 'transparent',
              color: activeTab === 'wifi' ? '#ffffff' : '#94a3b8',
              cursor: 'pointer',
            }}
          >
            Wi-Fi 設定
          </button>
          <button
            onClick={() => {
              setActiveTab('devices')
              setOpenMenuId(null)
            }}
            style={{
              padding: '4px 10px',
              fontSize: '0.775rem',
              fontWeight: 500,
              borderRadius: '6px',
              border: 'none',
              backgroundColor: activeTab === 'devices' ? '#334155' : 'transparent',
              color: activeTab === 'devices' ? '#ffffff' : '#94a3b8',
              cursor: 'pointer',
            }}
          >
            裝置管理
          </button>
          <button
            onClick={() => {
              setActiveTab('maintenance')
              setOpenMenuId(null)
            }}
            style={{
              padding: '4px 10px',
              fontSize: '0.775rem',
              fontWeight: 500,
              borderRadius: '6px',
              border: 'none',
              backgroundColor: activeTab === 'maintenance' ? '#334155' : 'transparent',
              color: activeTab === 'maintenance' ? '#ffffff' : '#94a3b8',
              cursor: 'pointer',
            }}
          >
            系統維護
          </button>
        </div>
      </div>

      {/* Main Content Viewport */}
      <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', minHeight: '380px' }}>
        {/* ================= 畫面一：系統維護頁 ================= */}
        {activeTab === 'maintenance' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Breadcrumb Navigation */}
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>路由器設定 / 系統維護</div>

            {/* Page Header */}
            <div>
              <h1
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#0f172a',
                  margin: '0 0 0.25rem',
                }}
              >
                系統維護
              </h1>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                重新啟動路由器，或將所有設定回復到出廠狀態。
              </p>
            </div>

            {/* Maintenance Status Feedback Banner */}
            {maintenanceStatus !== 'idle' && (
              <div
                style={{
                  backgroundColor: maintenanceStatus.includes('reset') ? '#fff1f2' : '#f0fdf4',
                  border: `1px solid ${maintenanceStatus.includes('reset') ? '#fca5a5' : '#86efac'}`,
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                }}
              >
                <span
                  style={{
                    fontSize: '13px',
                    color: maintenanceStatus.includes('reset') ? '#dc2626' : '#166534',
                    fontWeight: 500,
                  }}
                >
                  {maintenanceStatus === 'rebooting' && '正在重新啟動路由器，過程約需 1-2 分鐘...'}
                  {maintenanceStatus === 'reboot_done' && '重新啟動完成，網路連線已恢復。'}
                  {maintenanceStatus === 'resetting' && '正在將系統回復至原廠預設值...'}
                  {maintenanceStatus === 'reset_done' &&
                    '回復原廠設定完成，請以機身預設貼紙重新連線。'}
                </span>
                <button
                  onClick={() => setMaintenanceStatus('idle')}
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '3px 8px',
                    fontSize: '12px',
                    color: '#475569',
                    cursor: 'pointer',
                  }}
                >
                  關閉
                </button>
              </div>
            )}

            {/* Option Cards Stack */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Card 1: 重新啟動路由器 (Normal Gray Style) */}
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  {/* Circular icon container */}
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#475569',
                      fontSize: '18px',
                      flexShrink: 0,
                    }}
                  >
                    🔄
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#0f172a',
                        margin: '0 0 0.25rem',
                      }}
                    >
                      重新啟動路由器
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>
                      網路會短暫中斷約 1-2 分鐘，設定不會受影響。
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setRebootModalOpen(true)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    color: '#0f172a',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  重新啟動
                </button>
              </div>

              {/* Card 2: 回復原廠設定 (Orange-Red Warning Style) */}
              <div
                style={{
                  backgroundColor: '#fff1f2',
                  border: '1px solid #fca5a5',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  {/* Circular icon container (warning red) */}
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#dc2626',
                      fontSize: '18px',
                      flexShrink: 0,
                    }}
                  >
                    ⚠️
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#dc2626',
                        margin: '0 0 0.25rem',
                      }}
                    >
                      回復原廠設定
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#991b1b', lineHeight: 1.4 }}>
                      將清除所有自訂設定，包含 Wi-Fi 密碼與裝置清單，且無法復原。
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setFactoryModalOpen(true)
                    setFactoryChecked(false)
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #fca5a5',
                    color: '#dc2626',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  回復原廠
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= 畫面二：Wi-Fi 密碼設定頁 ================= */}
        {activeTab === 'wifi' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Breadcrumb Navigation */}
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>路由器設定 / Wi-Fi 設定</div>

            {/* Page Header */}
            <div>
              <h1
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#0f172a',
                  margin: '0 0 0.25rem',
                }}
              >
                設定 Wi-Fi
              </h1>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                修改名稱、密碼，查看誰在用你家網路。
              </p>
            </div>

            {/* Success Toast Banner */}
            {wifiToast && (
              <div
                style={{
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #6ee7b7',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  color: '#065f46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>✓</span>
                <span>{wifiToast}</span>
              </div>
            )}

            {/* Main Settings Card */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {/* SSID Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>
                  Wi-Fi 名稱
                </label>
                <input
                  type="text"
                  value={wifiSsid}
                  onChange={(e) => setWifiSsid(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 12px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    outline: 'none',
                    backgroundColor: '#ffffff',
                  }}
                />
              </div>

              {/* Password Input with Live Validation */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>
                  Wi-Fi 密碼
                </label>
                <input
                  type="text"
                  value={wifiPassword}
                  onChange={(e) => {
                    setWifiPassword(e.target.value)
                    if (showWifiErrorHighlight) setShowWifiErrorHighlight(false)
                  }}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 12px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    border: `1px solid ${
                      isPasswordError || showWifiErrorHighlight
                        ? '#dc2626'
                        : isPasswordValid
                          ? '#059669'
                          : '#cbd5e1'
                    }`,
                    backgroundColor:
                      isPasswordError || showWifiErrorHighlight ? '#fff1f2' : '#ffffff',
                    outline: 'none',
                  }}
                />

                {/* Validation Status Indicator */}
                {isPasswordValid && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <span>✓</span>
                    <span>密碼格式良好</span>
                  </div>
                )}
                {(isPasswordError || showWifiErrorHighlight) && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#dc2626',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <span>✕</span>
                    <span>密碼需至少 8 碼，建議包含英文與數字</span>
                  </div>
                )}
              </div>

              {/* Notice Remark Text */}
              <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.4 }}>
                改完密碼後，家中裝置需要重新輸入新密碼才能連線。
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveWifi}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#0f172a',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  marginTop: '4px',
                }}
              >
                儲存並套用
              </button>
            </div>

            {/* QR Code Share Card (Lightweight Sub-Card) */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>📱</span>
                <div>
                  <h4
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#0f172a',
                      margin: '0 0 0.15rem',
                    }}
                  >
                    用 QR Code 分享
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    朋友掃一下就能連線，不用念密碼。
                  </p>
                </div>
              </div>

              <button
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#0f172a',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                下載
              </button>
            </div>
          </div>
        )}

        {/* ================= 畫面三：裝置管理頁 ================= */}
        {activeTab === 'devices' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Breadcrumb Navigation */}
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>路由器設定 / 裝置管理</div>

            {/* Page Header */}
            <div>
              <h1
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#0f172a',
                  margin: '0 0 0.25rem',
                }}
              >
                已連線裝置
              </h1>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                共有 {devicesList.length} 台裝置正在使用你家 Wi-Fi。
              </p>
            </div>

            {/* Success Toast Banner */}
            {deviceToast && (
              <div
                style={{
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #6ee7b7',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  color: '#065f46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>✓</span>
                <span>{deviceToast}</span>
              </div>
            )}

            {/* Main Devices Card Encapsulating List */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              {devicesList.map((dev, idx) => (
                <div
                  key={dev.id}
                  style={{
                    padding: '14px 20px',
                    borderBottom: idx < devicesList.length - 1 ? '1px solid #f1f5f9' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    backgroundColor: dev.isBlocked ? '#fff1f2' : '#ffffff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '18px', color: '#475569' }}>{dev.icon}</span>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: dev.isBlocked ? '#dc2626' : '#0f172a',
                          }}
                        >
                          {dev.name}
                        </span>
                        {dev.isBlocked && (
                          <span
                            style={{
                              fontSize: '11px',
                              color: '#dc2626',
                              backgroundColor: '#fee2e2',
                              padding: '1px 5px',
                              borderRadius: '4px',
                            }}
                          >
                            已加入黑名單
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        {dev.type} · {dev.activity}
                      </div>
                    </div>
                  </div>

                  {/* Dropdown Menu Trigger */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setOpenMenuId(openMenuId === dev.id ? null : dev.id)}
                      aria-label="More options"
                      style={{
                        padding: '4px 10px',
                        fontSize: '16px',
                        fontWeight: 700,
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        color: '#64748b',
                        cursor: 'pointer',
                      }}
                    >
                      ⋯
                    </button>

                    {/* Popover Dropdown Menu */}
                    {openMenuId === dev.id && (
                      <div
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: '4px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                          zIndex: 50,
                          minWidth: '130px',
                          overflow: 'hidden',
                        }}
                      >
                        <button
                          onClick={() => {
                            setEditModalDevice(dev)
                            setEditNameInput(dev.name)
                            setOpenMenuId(null)
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            fontSize: '13px',
                            backgroundColor: '#ffffff',
                            border: 'none',
                            borderBottom: '1px solid #f1f5f9',
                            color: '#0f172a',
                            cursor: 'pointer',
                          }}
                        >
                          編輯名稱
                        </button>
                        <button
                          onClick={() => {
                            setBlockConfirmDevice(dev)
                            setOpenMenuId(null)
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            fontSize: '13px',
                            backgroundColor: '#ffffff',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                          }}
                        >
                          加入黑名單
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Tip Remark */}
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
              看到不認識的裝置？改個新密碼，或直接加入黑名單阻擋。
            </div>
          </div>
        )}
      </div>

      {/* ================= MODALS (OVERLAYS) ================= */}

      {/* 1. 重新啟動確認彈窗 */}
      {rebootModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '330px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
              確定要重新啟動嗎？
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
              過程約需 1-2 分鐘，期間網路會暫時中斷。
            </p>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}
            >
              <button
                onClick={() => setRebootModalOpen(false)}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                取消
              </button>
              <button
                onClick={handleConfirmReboot}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#0f172a',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                }}
              >
                確定重新啟動
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. 回復原廠確認彈窗 */}
      {factoryModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '340px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#dc2626', fontSize: '16px' }}>⚠️</span>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#dc2626', margin: 0 }}>
                回復原廠設定
              </h3>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
              此操作將清除所有設定（包含 Wi-Fi 密碼、裝置清單），且無法復原。
            </p>

            {/* Checkbox confirmation */}
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                cursor: 'pointer',
                marginTop: '4px',
              }}
            >
              <input
                type="checkbox"
                checked={factoryChecked}
                onChange={(e) => setFactoryChecked(e.target.checked)}
                style={{ marginTop: '2px' }}
              />
              <span style={{ fontSize: '12px', color: '#334155', lineHeight: 1.4 }}>
                我了解這會清除所有設定，且無法復原
              </span>
            </label>

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}
            >
              <button
                onClick={() => setFactoryModalOpen(false)}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                取消
              </button>
              <button
                disabled={!factoryChecked}
                onClick={handleConfirmFactoryReset}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: factoryChecked ? '#dc2626' : '#cbd5e1',
                  border: 'none',
                  color: '#ffffff',
                  cursor: factoryChecked ? 'pointer' : 'not-allowed',
                }}
              >
                回復原廠設定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. 裝置編輯名稱彈窗 */}
      {editModalDevice && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '330px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
              編輯裝置名稱
            </h3>
            <input
              type="text"
              value={editNameInput}
              onChange={(e) => setEditNameInput(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                fontSize: '14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                outline: 'none',
              }}
            />
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}
            >
              <button
                onClick={() => setEditModalDevice(null)}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                取消
              </button>
              <button
                onClick={handleSaveDeviceName}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#0f172a',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                }}
              >
                儲存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. 加入黑名單二次確認彈窗 */}
      {blockConfirmDevice && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '340px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#dc2626', fontSize: '16px' }}>⚠️</span>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#dc2626', margin: 0 }}>
                加入黑名單
              </h3>
            </div>
            <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.4 }}>
              <strong>{blockConfirmDevice.name}</strong>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                MAC: {blockConfirmDevice.mac}
              </div>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
              加入後，這台裝置將無法連上你家 Wi-Fi。
            </p>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}
            >
              <button
                onClick={() => setBlockConfirmDevice(null)}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                取消
              </button>
              <button
                onClick={handleConfirmBlockDevice}
                style={{
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                }}
              >
                加入黑名單
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SVGIcon({
  name,
  size = 16,
  color = 'currentColor',
  style,
}: {
  name: string
  size?: number
  color?: string
  style?: React.CSSProperties
}) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: '2',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style,
  }

  switch (name) {
    case 'home':
      return (
        <svg {...props}>
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      )
    case 'chart':
      return (
        <svg {...props}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      )
    case 'globe':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      )
    case 'wifi':
      return (
        <svg {...props}>
          <path d="M5 12.55a11 11 0 0 1 14.08 0" />
          <path d="M1.42 9a16 16 0 0 1 21.16 0" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" strokeWidth="3" />
        </svg>
      )
    case 'smartphone':
      return (
        <svg {...props}>
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2" />
        </svg>
      )
    case 'shield':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      )
    case 'refresh':
      return (
        <svg {...props}>
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
      )
    case 'settings':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      )
    case 'lock':
      return (
        <svg {...props}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      )
    case 'eye':
      return (
        <svg {...props}>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )
    case 'eye-off':
      return (
        <svg {...props}>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      )
    case 'zap':
      return (
        <svg {...props}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      )
    case 'alert-triangle':
      return (
        <svg {...props}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )
    case 'check-circle':
      return (
        <svg {...props}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      )
    case 'shield-alert':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )
    case 'edit':
      return (
        <svg {...props}>
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      )
    case 'trash':
      return (
        <svg {...props}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      )
    case 'search':
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      )
    case 'tv':
      return (
        <svg {...props}>
          <rect x="2" y="7" width="20" height="13" rx="2" ry="2" />
          <polyline points="17 2 12 7 7 2" />
        </svg>
      )
    case 'laptop':
      return (
        <svg {...props}>
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="2" y1="20" x2="22" y2="20" />
        </svg>
      )
    case 'plus':
      return (
        <svg {...props}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      )
    case 'list':
      return (
        <svg {...props}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      )
    default:
      return null
  }
}

/* ── 過濾模式分段控制器：沿用共用 SegmentedControl 的視覺，但支援「只允許」警示選中態 ── */
function FilterModeSegmented({
  value,
  onChange,
}: {
  value: 'off' | 'block' | 'allow'
  onChange: (m: 'off' | 'block' | 'allow') => void
}) {
  const options: { value: 'off' | 'block' | 'allow'; label: string; danger?: boolean }[] = [
    { value: 'off', label: '不啟用' },
    { value: 'block', label: '封鎖' },
    { value: 'allow', label: '只允許', danger: true },
  ]
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        backgroundColor: t.color.bgMuted,
        borderRadius: t.radius.pill,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            style={{
              fontFamily: t.font.family,
              fontSize: 14,
              fontWeight: active ? 500 : 400,
              color: active
                ? opt.danger
                  ? t.color.danger
                  : t.color.textPrimary
                : t.color.textSecondary,
              backgroundColor: active
                ? opt.danger
                  ? 'rgba(215,0,21,0.08)'
                  : t.color.bgCard
                : 'transparent',
              borderRadius: t.radius.pill,
              height: 40,
              padding: '0 20px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: active ? t.shadow.subtle : 'none',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/* ── 黑色藥丸主按鈕（設計系統 primary）── */
function PillButton({
  onClick,
  disabled,
  children,
}: {
  onClick?: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: t.font.family,
        fontSize: 15,
        fontWeight: 500,
        color: t.color.buttonPrimaryText,
        backgroundColor: t.color.buttonPrimary,
        borderRadius: t.radius.pill,
        height: 44,
        padding: '0 22px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.45 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      {children}
    </button>
  )
}

/* ── 表單輸入框（設計系統 TextInput 語彙：圓角 12 / 1px #D2D2D7 / focus 強調色）── */
function FilterInput({
  label,
  value,
  onChange,
  placeholder,
  mono,
  error,
  flex,
}: {
  label?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  mono?: boolean
  error?: string | null
  flex?: string
}) {
  const [focused, setFocused] = useState(false)
  const borderColor = error ? t.color.danger : focused ? t.color.accent : t.color.border
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex, minWidth: 0 }}>
      {label && (
        <label
          style={{
            fontFamily: t.font.family,
            fontSize: 13,
            fontWeight: 500,
            color: t.color.textPrimary,
          }}
        >
          {label}
        </label>
      )}
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          fontFamily: mono ? t.font.mono : t.font.family,
          fontSize: mono ? 14 : 15,
          color: t.color.textPrimary,
          height: 44,
          padding: '0 14px',
          borderRadius: t.radius.input,
          border: `1px solid ${borderColor}`,
          outline: 'none',
          backgroundColor: t.color.bgCard,
          width: '100%',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s ease',
        }}
      />
      {error && (
        <span style={{ fontFamily: t.font.family, fontSize: 12.5, color: t.color.danger }}>
          {error}
        </span>
      )}
    </div>
  )
}

function WifiFilterSettingsPage({
  initialMac,
  initialName,
}: {
  initialMac?: string
  initialName?: string
}) {
  // 過濾模式：不啟用 / 封鎖 / 只允許（點選即直接切換，不顯示灰色遮罩確認彈窗）
  const [mode, setMode] = useState<'off' | 'block' | 'allow'>('off')

  // 過濾名單與手動加入表單
  const [filterList, setFilterList] = useState([
    { id: 'fl1', name: '不明訪客裝置', mac: 'AA:BB:CC:11:22:33', date: '2026-08-01' },
    { id: 'fl2', name: '舊款印表機', mac: '44:55:66:77:88:99', date: '2026-08-02' },
  ])
  const [macInput, setMacInput] = useState(initialMac || '')
  const [nameInput, setNameInput] = useState(initialName || '')
  const [macError, setMacError] = useState<string | null>(null)
  const [urlParam, setUrlParam] = useState<{ mac: string; name: string } | null>(null)

  // 從 /wifi/clients 帶 ?mac=&name= 跳轉：自動預填手動加入區塊
  useEffect(() => {
    if (initialMac) {
      setMacInput(initialMac)
      setNameInput(initialName || '')
      setUrlParam({ mac: initialMac, name: initialName || '' })
      return
    }
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const mac = params.get('mac')
    if (mac) {
      const normalized = formatMacInput(mac)
      setMacInput(normalized)
      const name = params.get('name') || ''
      setNameInput(name)
      setUrlParam({ mac: normalized, name })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMac, initialName])

  // 右欄：目前線上裝置
  const [onlineDevices] = useState([
    {
      id: 'd1',
      name: '爸爸的 iPhone',
      mac: 'BC:D1:D3:4F:9A:12',
      ip: '192.168.1.101',
      iconName: 'smartphone',
    },
    {
      id: 'd2',
      name: '客廳 Smart TV',
      mac: 'A4:C3:F0:88:12:90',
      ip: '192.168.1.102',
      iconName: 'tv',
    },
    {
      id: 'd3',
      name: '媽媽的 iPad',
      mac: 'FE:88:12:34:56:78',
      ip: '192.168.1.103',
      iconName: 'smartphone',
    },
    {
      id: 'd4',
      name: '工作用 MacBook',
      mac: '70:A2:B4:90:11:22',
      ip: '192.168.1.104',
      iconName: 'laptop',
    },
    {
      id: 'd5',
      name: '智慧溫控感測器',
      mac: '12:34:56:78:9A:BC',
      ip: '192.168.1.105',
      iconName: 'zap',
    },
  ])
  const [searchQuery, setSearchQuery] = useState('')
  const [onlineOpen, setOnlineOpen] = useState(false) // 手機版展開狀態

  // 刪除（inline 二次確認）與極簡回饋
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [justAddedMac, setJustAddedMac] = useState<string | null>(null)
  const [applyFade, setApplyFade] = useState(false)

  // MAC 自動格式化：去除非十六進位字元 → 每 2 字加冒號 → 大寫
  const formatMacInput = (raw: string) => {
    const clean = raw
      .toUpperCase()
      .replace(/[^0-9A-F]/g, '')
      .slice(0, 12)
    return clean.replace(/(.{2})/g, '$1:').replace(/:$/, '')
  }
  const MAC_RE = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/
  const isMacValid = (v: string) => MAC_RE.test(v)

  const handleMacChange = (raw: string) => {
    const formatted = formatMacInput(raw)
    setMacInput(formatted)
    if (formatted.length > 0 && !isMacValid(formatted)) {
      setMacError('MAC 位址不完整，請輸入完整格式（例：A1:B2:C3:D4:E5:F6）')
    } else {
      setMacError(null)
    }
  }

  // 手動加入
  const handleAddManual = () => {
    if (!isMacValid(macInput)) {
      setMacError(
        macInput.length > 0
          ? 'MAC 位址不完整，請輸入完整格式（例：A1:B2:C3:D4:E5:F6）'
          : '請先輸入 MAC 位址'
      )
      return
    }
    if (filterList.some((f) => f.mac === macInput)) {
      setMacError('此 MAC 已在名單中')
      return
    }
    setFilterList((prev) => [
      {
        id: `fl_${Date.now()}`,
        name: nameInput.trim() || '未命名裝置',
        mac: macInput,
        date: '剛剛',
      },
      ...prev,
    ])
    setMacInput('')
    setNameInput('')
    setMacError(null)
    setUrlParam(null) // 已新增，提示列消失
  }

  // 一鍵加入線上裝置（不需輸入 MAC）
  const handleAddFromOnline = (dev: (typeof onlineDevices)[0]) => {
    if (filterList.some((f) => f.mac === dev.mac)) return
    setFilterList((prev) => [
      { id: `fl_${Date.now()}_${dev.id}`, name: dev.name, mac: dev.mac, date: '剛剛' },
      ...prev,
    ])
    setJustAddedMac(dev.mac)
    setTimeout(() => setJustAddedMac(null), 1600)
  }

  // 移除名單項目
  const handleDelete = (id: string) => {
    setFilterList((prev) => prev.filter((f) => f.id !== id))
    setConfirmDeleteId(null)
  }

  // 模式切換：點選即直接切換（不顯示灰色遮罩確認彈窗）
  const handleModeChange = (next: 'off' | 'block' | 'allow') => {
    setMode(next)
  }

  const handleApply = () => {
    setApplyFade(true)
    setTimeout(() => setApplyFade(false), 1600)
  }

  const isAllowEmpty = mode === 'allow' && filterList.length === 0
  const effectiveMode = mode

  const filteredOnline = onlineDevices.filter(
    (d) =>
      d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.mac.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.ip.includes(searchQuery)
  )

  const textLinkStyle: React.CSSProperties = {
    fontFamily: t.font.family,
    fontSize: 13,
    fontWeight: 500,
    color: t.color.accent,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  }

  // 右欄「目前線上裝置」面板（桌面固定側欄／手機展開後共用）
  const renderOnlinePanel = () => (
    <div style={{ backgroundColor: t.color.bgMuted, borderRadius: 16, padding: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 500, color: t.color.textPrimary }}>
          目前線上裝置
        </span>
        <span style={{ fontSize: 12.5, color: t.color.textSecondary }}>
          {onlineDevices.length} 台
        </span>
      </div>

      {/* 小搜尋框 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: '0 12px',
          borderRadius: t.radius.pill,
          backgroundColor: t.color.bgCard,
          border: '1px solid transparent',
          marginBottom: 8,
        }}
      >
        <SVGIcon name="search" size={14} color={t.color.textSecondary} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="搜尋線上裝置"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            fontSize: 13,
            color: t.color.textPrimary,
            fontFamily: t.font.family,
          }}
        />
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {filteredOnline.length === 0 ? (
          <li style={{ fontSize: 13, color: t.color.textSecondary, padding: '16px 0' }}>
            找不到符合的裝置
          </li>
        ) : (
          filteredOnline.map((d, idx) => {
            const inList = filterList.some((f) => f.mac === d.mac)
            return (
              <li
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 0',
                  borderTop: idx === 0 ? 'none' : `1px solid ${t.color.border}`,
                  opacity: inList ? 0.55 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: t.color.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {d.name}
                  </div>
                  <div style={{ fontSize: 12, color: t.color.textSecondary }}>{d.ip}</div>
                </div>
                {inList ? (
                  <span
                    style={{
                      fontSize: 12.5,
                      color: t.color.textSecondary,
                      whiteSpace: 'nowrap',
                      animation: justAddedMac === d.mac ? 'fadeInOut 1.6s ease forwards' : 'none',
                    }}
                  >
                    {justAddedMac === d.mac ? '✓ 已加入' : '已加入'}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleAddFromOnline(d)}
                    style={textLinkStyle}
                  >
                    + 加入
                  </button>
                )}
              </li>
            )
          })
        )}
      </ul>
    </div>
  )

  return (
    <div
      style={{
        fontFamily: t.font.family,
        color: t.color.textPrimary,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        maxWidth: 1080,
        margin: '0 auto',
        padding: '0 0 24px',
      }}
    >
      <style>{`
        .filter-cols {
          display: grid;
          grid-template-columns: minmax(0, 1.85fr) minmax(0, 1fr);
          gap: 16px;
          align-items: start;
        }
        .online-desktop { display: block; }
        .online-mobile { display: none; }
        @media (max-width: 768px) {
          .filter-cols { grid-template-columns: 1fr; }
          .online-desktop { display: none; }
          .online-mobile { display: block; }
        }
      `}</style>

      {/* ===== 頁首：標題 + 白話說明 ===== */}
      <header>
        <h1
          style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: t.color.textPrimary,
          }}
        >
          Wi-Fi 過濾名單設定
        </h1>
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 13,
            color: t.color.textSecondary,
            lineHeight: 1.5,
          }}
        >
          設定哪些裝置可以連上你的 Wi-Fi，避免不明裝置蹭網。
        </p>
      </header>

      {/* ===== 1. 模式選擇（分段控制器，非並排卡片） ===== */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ fontSize: 13, color: t.color.textSecondary }}>過濾模式</span>
        <FilterModeSegmented value={effectiveMode} onChange={handleModeChange} />
        {effectiveMode === 'allow' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: t.color.danger,
            }}
          >
            <SVGIcon name="alert-triangle" size={14} color={t.color.danger} />
            只允許模式：不在名單上的裝置都將無法連上 Wi-Fi
          </div>
        )}
      </section>

      {/* ===== 邊界情境：只允許模式且名單為空 ===== */}
      {isAllowEmpty && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: t.color.danger,
          }}
        >
          <SVGIcon name="alert-triangle" size={14} color={t.color.danger} />
          名單目前是空的，啟用後所有裝置都無法連線
        </div>
      )}

      {/* ===== 2. 名單管理區（左 65% / 右 35%） ===== */}
      <div className="filter-cols">
        {/* 左欄：過濾名單 */}
        <div
          style={{
            backgroundColor: t.color.bgCard,
            borderRadius: 14,
            border: `1px solid ${t.color.border}`,
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 500, color: t.color.textPrimary }}>
            過濾名單
            <span
              style={{
                fontSize: 12.5,
                color: t.color.textSecondary,
                fontWeight: 400,
                marginLeft: 8,
              }}
            >
              {filterList.length} 筆
            </span>
          </div>

          {/* 帶 MAC 跳轉的提示列 */}
          {urlParam && (
            <div
              style={{
                backgroundColor: t.color.bgMuted,
                borderRadius: 12,
                padding: '10px 14px',
                fontSize: 13,
                color: t.color.textPrimary,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <span>
                已為你帶入裝置：
                <strong>{urlParam.name || '未命名裝置'}</strong>
                ，請確認後新增
              </span>
            </div>
          )}

          {/* 手動加入（MAC 自動格式化 + 即時驗證 + 名稱選填） */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <FilterInput
                label="MAC 位址"
                placeholder="A1:B2:C3:D4:E5:F6"
                mono
                value={macInput}
                onChange={handleMacChange}
                error={macError}
              />
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <FilterInput
                label="裝置名稱（選填）"
                placeholder="例如：隔壁的電視"
                value={nameInput}
                onChange={setNameInput}
              />
            </div>
            <div style={{ paddingBottom: 12 }}>
              <button type="button" onClick={handleAddManual} style={textLinkStyle}>
                新增
              </button>
            </div>
          </div>

          {/* 名單清單（細分隔線樣式） */}
          {filterList.length === 0 ? (
            <div style={{ fontSize: 13, color: t.color.textSecondary, padding: '12px 0' }}>
              名單目前是空的
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {filterList.map((f, idx) => (
                <li
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '12px 0',
                    borderTop: idx === 0 ? 'none' : `1px solid ${t.color.border}`,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 500,
                        color: t.color.textPrimary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {f.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: t.color.textSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontFamily: t.font.mono }}>{f.mac}</span>
                      <span style={{ color: t.color.border }}>·</span>
                      <span>加入於 {f.date}</span>
                    </div>
                  </div>

                  {confirmDeleteId === f.id ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 12.5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ color: t.color.textSecondary }}>確定移除？</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(f.id)}
                        style={textLinkStyle}
                      >
                        確認
                      </button>
                      <span style={{ color: t.color.border }}>／</span>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        style={{ ...textLinkStyle, color: t.color.textSecondary }}
                      >
                        取消
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(f.id)}
                      aria-label={`移除 ${f.name}`}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: t.color.textSecondary,
                        transition: 'background-color 0.15s ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = t.color.bgMuted)
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <SVGIcon name="trash" size={14} color={t.color.textSecondary} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 右欄：目前線上裝置（桌面固定側欄） */}
        <div className="online-desktop">{renderOnlinePanel()}</div>

        {/* 右欄（手機版：預設收合的可展開列） */}
        <div className="online-mobile">
          <button
            type="button"
            onClick={() => setOnlineOpen((o) => !o)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              backgroundColor: t.color.bgMuted,
              borderRadius: 16,
              border: 'none',
              cursor: 'pointer',
              fontFamily: t.font.family,
              fontSize: 14,
              fontWeight: 500,
              color: t.color.textPrimary,
            }}
          >
            <span>查看目前線上裝置（可快速加入）</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                transition: 'transform 0.2s ease',
                transform: onlineOpen ? 'rotate(180deg)' : 'none',
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="#86868B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {onlineOpen && <div style={{ marginTop: 10 }}>{renderOnlinePanel()}</div>}
        </div>
      </div>

      {/* ===== 3. 套用 ===== */}
      <footer
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {applyFade && (
          <span
            style={{
              fontSize: 12.5,
              color: t.color.success,
              animation: 'fadeInOut 1.6s ease forwards',
            }}
          >
            ✓ 已套用
          </span>
        )}
        {isAllowEmpty && (
          <span style={{ fontSize: 12.5, color: t.color.textSecondary }}>
            名單是空的，無法啟用只允許模式
          </span>
        )}
        <PillButton disabled={isAllowEmpty} onClick={handleApply}>
          套用
        </PillButton>
      </footer>

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  )
}

function WifiSettingsFullPage() {
  // Navigation Menu Data Structure
  const menuGroups = [
    {
      title: '網路狀態總覽',
      icon: 'chart',
      items: [
        '快速設定',
        '網路連線診斷',
        '系統資訊',
        '告警狀態',
        '光纖連線狀態（PON）',
        '介面／路由狀態',
      ],
    },
    {
      title: '設定上網連線',
      icon: 'globe',
      items: [
        'WAN 連線設定',
        'DNS 設定',
        'DNS 路由',
        '路由設定(IPv4/IPv6)',
        '動態 DNS 設定（DDNS）',
      ],
    },
    {
      title: '設定 Wi-Fi',
      icon: 'wifi',
      items: ['Wi-Fi 基本 / 加密設定', 'Wi-Fi 過濾名單設定', '連線裝置資訊（Wi-Fi 客戶端）'],
    },
    {
      title: '管理家中裝置',
      icon: 'smartphone',
      items: [
        '連線裝置清單',
        'LAN設定(IPv4/IPv6)',
        'DHCP 租借時間設定',
        '連接埠 MAC 位址資訊',
        'LAN 連接埠設定',
      ],
    },
    {
      title: '保護我的網路',
      icon: 'shield',
      items: [
        '裝置封鎖／黑名單設定（Layer2/3 Filter）',
        '連接埠轉發（Port Forwarding）',
        'NAT ALG／流量分類設定',
        'MAC 控制清單（MCL）— 遠端管理權限設定',
        '其他安全設定',
      ],
    },
    {
      title: '系統更新與維護',
      icon: 'refresh',
      items: [
        '韌體更新',
        '設定檔備份與回復',
        '回復出廠設定',
        '重新啟動',
        '系統設定',
        '系統紀錄（System Log）',
        '封包統計',
      ],
    },
    {
      title: '進階設定（專業使用者）',
      icon: 'settings',
      items: [
        '註冊ID(SLID)',
        'SNMP設定',
        'TR069管理',
        '供裝模式設定',
        'IGMP設定',
        'TLS設定',
        'CFM Action',
        'CFM Status',
      ],
    },
  ]

  // 三個核心可互動頁面（Wi-Fi 基本/加密設定、連線裝置資訊、過濾名單設定）
  // 其餘所有子選單皆鎖定為灰色、不可點擊（僅供展示架構）
  const enabledMenuItems = new Set([
    'Wi-Fi 基本 / 加密設定',
    'Wi-Fi 過濾名單設定',
    '連線裝置資訊（Wi-Fi 客戶端）',
  ])

  // Sidebar States
  // 預設只展開「設定 Wi-Fi」，其餘群組全部收合；頁面固定在「Wi-Fi 基本 / 加密設定」
  const [activeMenuItem, setActiveMenuItem] = useState('Wi-Fi 基本 / 加密設定')
  const [openGroups, setOpenGroups] = useState<string[]>(['設定 Wi-Fi'])

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    )
  }

  // Form States
  const [ssid, setSsid] = useState('Chunghwa_Home_5G')
  const [password, setPassword] = useState('K9#mP2$xR8')
  const [showPassword, setShowPassword] = useState(false)
  const [activeBand, setActiveBand] = useState<'2.4G' | '5G' | '6G'>('5G')
  const [applyToAllBands, setApplyToAllBands] = useState(true)
  const [encryption, setEncryption] = useState('wpa2')
  const [hideSsid, setHideSsid] = useState(false)

  // System & Flow States
  const [isSaving, setIsSaving] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  // Devices Preview Data (Top 5 connected devices)
  const [devices, setDevices] = useState([
    {
      id: 'd1',
      name: '爸爸的 iPhone',
      mac: 'BC:D1:D3:4F:9A:12',
      ip: '192.168.1.101',
      band: '5GHz',
      time: '2 小時前',
      iconName: 'smartphone',
    },
    {
      id: 'd2',
      name: '客廳 Smart TV',
      mac: 'A4:C3:F0:88:12:90',
      ip: '192.168.1.102',
      band: 'LAN 1 (有線)',
      time: '5 小時前',
      iconName: 'tv',
    },
    {
      id: 'd3',
      name: '媽媽的 iPad',
      mac: 'FE:88:12:34:56:78',
      ip: '192.168.1.103',
      band: '2.4GHz',
      time: '30 分鐘前',
      iconName: 'smartphone',
    },
    {
      id: 'd4',
      name: '工作用 MacBook',
      mac: '70:A2:B4:90:11:22',
      ip: '192.168.1.104',
      band: '5GHz',
      time: '15 分鐘前',
      iconName: 'laptop',
    },
    {
      id: 'd5',
      name: '智慧溫控感測器',
      mac: '12:34:56:78:9A:BC',
      ip: '192.168.1.105',
      band: '2.4GHz',
      time: '1 天前',
      iconName: 'zap',
    },
  ])

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editNameVal, setEditNameVal] = useState('')

  // Calculate Password Strength
  const getPasswordStrength = (pass: string) => {
    if (!pass || pass.length < 8) {
      return {
        level: 'weak',
        label: '弱 (少於 8 碼，防護等級低)',
        color: t.color.danger,
        bg: 'rgba(215,0,21,0.05)',
        percent: 25,
      }
    }
    const hasUpper = /[A-Z]/.test(pass)
    const hasLower = /[a-z]/.test(pass)
    const hasNum = /[0-9]/.test(pass)
    const hasSymbol = /[#$!%*?&]/.test(pass)

    if (pass.length >= 10 && (hasUpper || hasSymbol) && hasNum && hasLower) {
      return {
        level: 'strong',
        label: '強 (高度防護等級)',
        color: t.color.success,
        bg: 'rgba(29,185,84,0.1)',
        percent: 100,
      }
    }
    return {
      level: 'medium',
      label: '中 (符合基本長度)',
      color: t.color.accent,
      bg: 'rgba(0,102,204,0.06)',
      percent: 60,
    }
  }

  const strength = getPasswordStrength(password)

  // Generate Random High-Strength Password
  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789#$%'
    let newPass = ''
    for (let i = 0; i < 11; i++) {
      newPass += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setPassword(newPass)
  }

  // Handle Form Save
  const handleSave = () => {
    setIsSaving(true)
    setTimeout(() => {
      setIsSaving(false)
      setToastMessage('✓ Wi-Fi 設定已更新，部分家中裝置可能需要重新輸入新密碼連線！')
      setTimeout(() => setToastMessage(null), 3500)
    }, 1200)
  }

  // Edit Device Alias Name
  const handleSaveDeviceName = (id: string) => {
    if (!editNameVal.trim()) return
    setDevices((prev) => prev.map((d) => (d.id === id ? { ...d, name: editNameVal.trim() } : d)))
    setEditingId(null)
  }

  return (
    <div
      className="wifi-demo-container"
      style={{
        backgroundColor: t.color.bgCard,
        border: `1px solid ${t.color.border}`,
        borderRadius: t.radius.cardLowDensity,
        boxShadow: '0 18px 48px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        fontSize: t.type.body.fontSize,
        color: t.color.textPrimary,
        fontFamily: t.font.family,
        // 16:9 一般螢幕比例：高度隨容器寬度等比縮放（1200px 寬時約 675px 高）
        width: '100%',
        aspectRatio: '16 / 9',
        minHeight: 520,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{`
        /* ---- Apple.com 質感元件狀態（default / hover / active / focus / disabled） ---- */
        .rs-btn {
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease,
            opacity 0.15s ease, transform 0.06s ease, box-shadow 0.15s ease;
        }
        .rs-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .rs-primary {
          background-color: #1D1D1F;
          color: #FFFFFF;
          border: none;
        }
        .rs-primary:hover:not(:disabled) {
          background-color: rgba(29, 29, 31, 0.82);
        }
        .rs-primary:active:not(:disabled) {
          background-color: #000000;
          transform: scale(0.98);
        }
        .rs-primary:focus-visible {
          outline: 2px solid #0066CC;
          outline-offset: 2px;
        }
        .rs-secondary {
          background-color: #FFFFFF;
          color: #1D1D1F;
          border: 1px solid #D2D2D7;
        }
        .rs-secondary:hover:not(:disabled) {
          background-color: #F5F5F7;
        }
        .rs-secondary:active:not(:disabled) {
          background-color: rgba(29, 29, 31, 0.06);
          transform: scale(0.98);
        }
        .rs-secondary:focus-visible {
          outline: 2px solid #0066CC;
          outline-offset: 2px;
        }
        .rs-danger-outline {
          background-color: rgba(215, 0, 21, 0.06);
          color: #D70015;
          border: 1px solid rgba(215, 0, 21, 0.3);
        }
        .rs-danger-outline:hover:not(:disabled) {
          background-color: rgba(215, 0, 21, 0.12);
        }
        .rs-danger-outline:active:not(:disabled) {
          background-color: rgba(215, 0, 21, 0.18);
          transform: scale(0.98);
        }
        .rs-danger-outline:focus-visible {
          outline: 2px solid #D70015;
          outline-offset: 2px;
        }
        .rs-link {
          background: none;
          border: none;
          color: #0066CC;
        }
        .rs-link:hover:not(:disabled) {
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .rs-link:focus-visible {
          outline: 2px solid #0066CC;
          outline-offset: 2px;
          border-radius: 4px;
        }
        .rs-input {
          border: 1px solid #D2D2D7;
          border-radius: 12px;
          background-color: #FFFFFF;
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
        }
        .rs-input:focus {
          border-color: #0066CC;
          box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.15);
        }
        .rs-input::placeholder {
          color: #86868B;
        }
        .rs-side-group {
          transition: background-color 0.15s ease;
        }
        .rs-side-group:hover:not(:disabled) {
          background-color: #F5F5F7;
        }
        .rs-side-item {
          transition: background-color 0.15s ease;
        }
        .rs-side-item:hover:not(:disabled) {
          background-color: #F5F5F7;
        }
        .rs-seg {
          background-color: #F5F5F7;
          border-radius: 999px;
          padding: 3px;
          display: flex;
          gap: 2px;
        }
        .rs-seg-btn {
          flex: 1;
          border: none;
          background-color: transparent;
          color: #86868B;
          border-radius: 999px;
          padding: 8px 10px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          font-family: inherit;
          transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
        }
        .rs-seg-btn:hover {
          color: #1D1D1F;
        }
        .rs-seg-btn.active {
          background-color: #FFFFFF;
          color: #1D1D1F;
          font-weight: 600;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }
        .rs-seg-btn:focus-visible {
          outline: 2px solid #0066CC;
          outline-offset: 1px;
        }
      `}</style>

      {/* 1. Browser Window Top Frame Bar */}
      <div
        style={{
          backgroundColor: t.color.buttonPrimary,
          color: t.color.buttonPrimaryText,
          padding: '0.6rem 1.1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '7px' }}>
            <span
              style={{
                width: '11px',
                height: '11px',
                borderRadius: '50%',
                backgroundColor: '#FF5F57',
              }}
            />
            <span
              style={{
                width: '11px',
                height: '11px',
                borderRadius: '50%',
                backgroundColor: '#FEBC2E',
              }}
            />
            <span
              style={{
                width: '11px',
                height: '11px',
                borderRadius: '50%',
                backgroundColor: '#28C840',
              }}
            />
          </div>
        </div>
        <div style={{ fontSize: '12.5px', fontWeight: 500, color: 'rgba(255,255,255,0.85)' }}></div>
        <div style={{ width: '60px' }} />
      </div>

      {/* Main Container Area (Sidebar + Content Grid) */}
      <div
        style={{
          display: 'flex',
          flex: 1,
          backgroundColor: t.color.bgMuted,
          overflow: 'hidden',
        }}
      >
        {/* ================= Left Sidebar Management Menu ================= */}
        <aside
          style={{
            width: '264px',
            backgroundColor: t.color.bgCard,
            borderRight: `1px solid ${t.color.border}`,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            overflowY: 'auto',
          }}
        >
          {/* Sidebar Top Home Header */}
          <div
            style={{
              padding: '18px 20px 14px',
              borderBottom: `1px solid ${t.color.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                fontWeight: 600,
                fontSize: '14.5px',
                color: t.color.textPrimary,
                letterSpacing: '-0.01em',
              }}
            >
              <SVGIcon name="home" size={17} color={t.color.textPrimary} />
              <span>家用光世代路由器管理後台</span>
            </div>
          </div>

          {/* Sidebar Accordion Groups */}
          <div
            style={{
              padding: '10px 10px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {menuGroups.map((group) => {
              const isOpen = openGroups.includes(group.title)
              return (
                <div key={group.title} style={{ display: 'flex', flexDirection: 'column' }}>
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.title)}
                    className="rs-side-group"
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: t.color.textPrimary,
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <SVGIcon name={group.icon} size={15} color={t.color.textSecondary} />
                      <span>{group.title}</span>
                    </div>
                    <span style={{ fontSize: '9px', color: t.color.textSecondary }}>
                      {isOpen ? '▼' : '▶'}
                    </span>
                  </button>

                  {/* Submenu Items */}
                  {isOpen && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        paddingLeft: '24px',
                        marginTop: '2px',
                      }}
                    >
                      {group.items.map((item) => {
                        const isActive = activeMenuItem === item
                        const isEnabled = enabledMenuItems.has(item)
                        return (
                          <button
                            key={item}
                            type="button"
                            disabled={!isEnabled}
                            onClick={() => {
                              setActiveMenuItem(item)
                            }}
                            className="rs-side-item"
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              fontSize: '12.5px',
                              fontWeight: isActive ? 500 : 400,
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: isActive ? 'rgba(0,102,204,0.08)' : 'transparent',
                              color: !isEnabled
                                ? 'rgba(134,134,139,0.45)'
                                : isActive
                                  ? t.color.accent
                                  : t.color.textSecondary,
                              cursor: !isEnabled ? 'not-allowed' : 'pointer',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              lineHeight: 1.4,
                            }}
                          >
                            <span>{item}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </aside>

        {/* ================= Right Main Content Area ================= */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            padding: '26px 30px',
            backgroundColor: t.color.bgMuted,
            overflowY: 'auto',
          }}
        >
          {activeMenuItem === 'Wi-Fi 過濾名單設定' || activeMenuItem.includes('Layer2/3 Filter') ? (
            <WifiFilterSettingsPage />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Global Toast Banner */}
              {toastMessage && (
                <div
                  style={{
                    backgroundColor: 'rgba(29,185,84,0.08)',
                    border: '1px solid rgba(29,185,84,0.35)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '13.5px',
                    color: t.color.textPrimary,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <CheckCircle size={16} color={t.color.success} />
                  <span>{toastMessage.replace(/^✓\s*/, '')}</span>
                </div>
              )}

              {/* Page Title Header */}
              <div>
                <h1
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: t.color.textPrimary,
                    margin: '0 0 6px',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {activeMenuItem === '連線裝置資訊（Wi-Fi 客戶端）'
                    ? '連線裝置資訊（Wi-Fi 客戶端）'
                    : 'Wi-Fi 基本 / 加密設定'}
                </h1>
                <p style={{ margin: 0, fontSize: 14.5, color: t.color.textSecondary }}>
                  {activeMenuItem === '連線裝置資訊（Wi-Fi 客戶端）'
                    ? '檢視目前連線到此 Wi-Fi 的裝置清單，可編輯別名並快速帶入過濾名單。'
                    : '設定您家中的無線網路名稱與通行密碼，兼顧新手白話易用與工程師彈性配備需求。'}
                </p>
              </div>

              {/* 12-Column Responsive Layout Grid */}
              {activeMenuItem === 'Wi-Fi 基本 / 加密設定' && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '20px',
                  }}
                >
                  {/* Left Column (8 Columns in Desktop): Wi-Fi Settings Form */}
                  <div
                    style={{
                      gridColumn: 'span 2 / span 2',
                      backgroundColor: t.color.bgCard,
                      border: `1px solid ${t.color.border}`,
                      borderRadius: t.radius.cardLowDensity,
                      padding: '28px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                      boxShadow: t.shadow.subtle,
                    }}
                  >
                    <h2
                      style={{
                        fontSize: t.type.cardTitle.fontSize,
                        fontWeight: t.type.cardTitle.fontWeight,
                        color: t.color.textPrimary,
                        margin: 0,
                        paddingBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '9px',
                      }}
                    >
                      <SVGIcon name="settings" size={17} color={t.color.textPrimary} />
                      <span>無線網路主要設定表單</span>
                    </h2>

                    {/* 1. SSID Field */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label
                        htmlFor="wifi-ssid-input"
                        style={{ fontSize: 14, fontWeight: 500, color: t.color.textPrimary }}
                      >
                        Wi-Fi 名稱 (SSID)
                      </label>
                      <input
                        id="wifi-ssid-input"
                        type="text"
                        value={ssid}
                        onChange={(e) => {
                          setSsid(e.target.value)
                        }}
                        className="rs-input"
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '0 14px',
                          height: 46,
                          fontSize: 15,
                          fontFamily: t.font.family,
                        }}
                      />
                      <span
                        style={{ fontSize: t.type.caption.fontSize, color: t.color.textSecondary }}
                      >
                        輔助說明：此名稱將公開顯示於手機與電腦的 Wi-Fi 搜尋清單中。
                      </span>
                    </div>

                    {/* 2. Password Field with Eye Toggle & Generate Strong Password Button */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <label
                          htmlFor="wifi-password-input"
                          style={{ fontSize: 14, fontWeight: 500, color: t.color.textPrimary }}
                        >
                          Wi-Fi 密碼 (Passphrase)
                        </label>
                        <button
                          type="button"
                          onClick={handleGeneratePassword}
                          className="rs-link"
                          style={{
                            fontSize: 12.5,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            cursor: 'pointer',
                            padding: 0,
                            fontFamily: t.font.family,
                          }}
                        >
                          <SVGIcon name="zap" size={13} color={t.color.accent} />
                          <span>產生強密碼</span>
                        </button>
                      </div>

                      <div style={{ position: 'relative', width: '100%' }}>
                        <input
                          id="wifi-password-input"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value)
                          }}
                          className="rs-input"
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '0 44px 0 14px',
                            height: 46,
                            fontSize: 15,
                            fontFamily: t.font.family,
                            borderColor: strength.level === 'weak' ? t.color.danger : undefined,
                            backgroundColor:
                              strength.level === 'weak' ? strength.bg : t.color.bgCard,
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? '隱藏密碼' : '顯示密碼'}
                          style={{
                            position: 'absolute',
                            right: '14px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: t.color.textSecondary,
                          }}
                        >
                          <SVGIcon
                            name={showPassword ? 'eye-off' : 'eye'}
                            size={18}
                            color={t.color.textSecondary}
                          />
                        </button>
                      </div>

                      {/* Password Strength Meter */}
                      <div
                        style={{
                          marginTop: '2px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: 12.5,
                          }}
                        >
                          <span style={{ color: t.color.textSecondary }}>密碼防護強度：</span>
                          <span style={{ fontWeight: 500, color: strength.color }}>
                            {strength.label}
                          </span>
                        </div>
                        <div
                          style={{
                            width: '100%',
                            height: 6,
                            backgroundColor: t.color.bgMuted,
                            borderRadius: 99,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${strength.percent}%`,
                              height: '100%',
                              backgroundColor: strength.color,
                              borderRadius: 99,
                              transition: 'all 0.3s ease',
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 3. Encryption Method Selector (Dual Technical & Plain Terms) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label
                        htmlFor="wifi-encryption-select"
                        style={{ fontSize: 14, fontWeight: 500, color: t.color.textPrimary }}
                      >
                        加密驗證方式 (Encryption Method)
                      </label>
                      <select
                        id="wifi-encryption-select"
                        value={encryption}
                        onChange={(e) => {
                          setEncryption(e.target.value)
                        }}
                        className="rs-input"
                        style={{
                          width: '100%',
                          height: 46,
                          padding: '0 12px',
                          fontSize: 15,
                          fontFamily: t.font.family,
                          color: t.color.textPrimary,
                        }}
                      >
                        <option value="wpa3">加密方式（WPA3，較新較安全 - 推薦）</option>
                        <option value="wpa2">加密方式（WPA2，相容性較好 - 標準）</option>
                        <option value="wpa2wpa3">加密方式（WPA2/WPA3 混合模式）</option>
                        <option value="open">無加密（開放網路，不安全）</option>
                      </select>
                    </div>

                    {/* 4. Frequency Band Switch & Apply All Toggle */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{ fontSize: 14, fontWeight: 500, color: t.color.textPrimary }}>
                        無線頻段切換與頻道同步
                      </label>
                      <div className="rs-seg" role="group" aria-label="無線頻段">
                        {(['2.4G', '5G', '6G'] as const).map((band) => (
                          <button
                            key={band}
                            type="button"
                            onClick={() => setActiveBand(band)}
                            className={`rs-seg-btn${activeBand === band ? ' active' : ''}`}
                          >
                            {band} {band === '5G' ? '(常用)' : ''}
                          </button>
                        ))}
                      </div>

                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '9px',
                          fontSize: 13.5,
                          color: t.color.textPrimary,
                          cursor: 'pointer',
                          marginTop: '2px',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={applyToAllBands}
                          onChange={(e) => {
                            setApplyToAllBands(e.target.checked)
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: t.color.accent,
                            cursor: 'pointer',
                          }}
                        />
                        <span>套用到所有頻段 (2.4GHz / 5GHz / 6GHz 同步密碼與名稱)</span>
                      </label>
                    </div>

                    {/* 5. Hide SSID Broadcast Switch */}
                    <div
                      style={{
                        backgroundColor: t.color.bgMuted,
                        padding: '14px 16px',
                        borderRadius: t.radius.input,
                      }}
                    >
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '9px',
                          fontSize: 13.5,
                          fontWeight: 500,
                          color: t.color.textPrimary,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={hideSsid}
                          onChange={(e) => {
                            setHideSsid(e.target.checked)
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: t.color.accent,
                            cursor: 'pointer',
                          }}
                        />
                        <span>隱藏網路名稱 (隱藏 SSID 廣播)</span>
                      </label>
                      <p
                        style={{
                          margin: '6px 0 0 25px',
                          fontSize: 12,
                          color: t.color.textSecondary,
                          lineHeight: 1.5,
                        }}
                      >
                        輔助說明：開啟後裝置需手動輸入名稱才能連線，不建議一般使用者開啟。
                      </p>
                    </div>

                    {/* 6. Form Submit / Cancel Action Bar */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        marginTop: '6px',
                        paddingTop: '20px',
                        borderTop: `1px solid ${t.color.border}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={isSaving}
                          className="rs-btn rs-primary"
                          style={{
                            height: 44,
                            padding: '0 24px',
                            fontSize: 15,
                            fontWeight: 500,
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontFamily: t.font.family,
                          }}
                        >
                          {isSaving ? '儲存中...' : '儲存變更'}
                        </button>
                        <button
                          type="button"
                          className="rs-btn rs-secondary"
                          style={{
                            height: 44,
                            padding: '0 20px',
                            fontSize: 15,
                            fontWeight: 500,
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: t.font.family,
                          }}
                        >
                          取消
                        </button>
                      </div>

                      {/* Warning Notice if Password Strength is Weak */}
                      {strength.level === 'weak' && (
                        <div
                          style={{
                            fontSize: 12.5,
                            color: t.color.danger,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <SVGIcon name="alert-triangle" size={14} color={t.color.danger} />
                          <span>目前密碼強度較低，建議使用強密碼</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 12-Column Full-Width Bottom Section: Connected Devices Preview Table */}
              {activeMenuItem === '連線裝置資訊（Wi-Fi 客戶端）' && (
                <div
                  style={{
                    backgroundColor: t.color.bgCard,
                    border: `1px solid ${t.color.border}`,
                    borderRadius: t.radius.cardLowDensity,
                    padding: '24px 28px 28px',
                    boxShadow: t.shadow.subtle,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: t.type.cardTitle.fontSize,
                          fontWeight: t.type.cardTitle.fontWeight,
                          color: t.color.textPrimary,
                          margin: '0 0 4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <SVGIcon name="smartphone" size={17} color={t.color.textPrimary} />
                        <span>目前連線裝置預覽 (Connected Devices)</span>
                      </h3>
                      <p style={{ margin: 0, fontSize: 12.5, color: t.color.textSecondary }}>
                        僅顯示近期線上裝置前 5 筆，提供別名編輯與一鍵帶入過濾名單功能。
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setToastMessage('已模擬導向 /wifi/clients 完整裝置管理頁面。')
                        setTimeout(() => setToastMessage(null), 2500)
                      }}
                      className="rs-link"
                      style={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        cursor: 'pointer',
                        padding: 0,
                        fontFamily: t.font.family,
                      }}
                    >
                      查看全部裝置 <span style={{ fontSize: 15 }}>→</span>
                    </button>
                  </div>

                  {/* Table Container */}
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13.5,
                        textAlign: 'left',
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: `1px solid ${t.color.border}`,
                            color: t.color.textSecondary,
                            fontSize: 12,
                          }}
                        >
                          <th style={{ padding: '10px 14px', fontWeight: 500 }}>裝置名稱</th>
                          <th style={{ padding: '10px 14px', fontWeight: 500 }}>IP 位址</th>
                          <th style={{ padding: '10px 14px', fontWeight: 500 }}>頻段 / 介面</th>
                          <th style={{ padding: '10px 14px', fontWeight: 500 }}>連線時間</th>
                          <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>
                            操作項
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((dev) => (
                          <tr
                            key={dev.id}
                            style={{ borderBottom: '1px solid rgba(210,210,215,0.5)' }}
                          >
                            <td
                              style={{
                                padding: '14px',
                                fontWeight: 500,
                                color: t.color.textPrimary,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <SVGIcon
                                  name={dev.iconName}
                                  size={17}
                                  color={t.color.textSecondary}
                                />
                                {editingId === dev.id ? (
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                      type="text"
                                      value={editNameVal}
                                      onChange={(e) => setEditNameVal(e.target.value)}
                                      className="rs-input"
                                      style={{
                                        padding: '0 10px',
                                        height: 32,
                                        fontSize: 12.5,
                                        width: 140,
                                        fontFamily: t.font.family,
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleSaveDeviceName(dev.id)}
                                      className="rs-btn rs-primary"
                                      style={{
                                        height: 32,
                                        padding: '0 12px',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        borderRadius: 999,
                                        fontFamily: t.font.family,
                                      }}
                                    >
                                      儲存
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                  >
                                    <span>{dev.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingId(dev.id)
                                        setEditNameVal(dev.name)
                                      }}
                                      className="rs-link"
                                      aria-label={`編輯 ${dev.name} 名稱`}
                                      style={{
                                        fontSize: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                    >
                                      <SVGIcon
                                        name="edit"
                                        size={14}
                                        color={t.color.textSecondary}
                                      />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: '14px',
                                color: t.color.textSecondary,
                                fontFamily: t.font.mono,
                                fontSize: 13,
                              }}
                            >
                              {dev.ip}
                            </td>
                            <td style={{ padding: '14px', color: t.color.textSecondary }}>
                              {dev.band}
                            </td>
                            <td style={{ padding: '14px', color: t.color.textSecondary }}>
                              {dev.time}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setToastMessage(`已帶入 MAC (${dev.mac}) 並模擬導向 /wifi/filter`)
                                  setTimeout(() => setToastMessage(null), 3000)
                                }}
                                className="rs-btn rs-danger-outline"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  borderRadius: 999,
                                  padding: '7px 14px',
                                  height: 32,
                                  cursor: 'pointer',
                                  fontFamily: t.font.family,
                                }}
                              >
                                加入過濾名單
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default function RouterUxOptimization({ proj: _proj }: Props = {}) {
  const router = useRouter()
  const heuristicScores = [
    { name: '系統狀態的可見性', score: 4.0, category: 'good' },
    { name: '系統與真實世界的匹配', score: 2.5, category: 'warning' },
    { name: '使用者控制與自由', score: 3.5, category: 'good' },
    { name: '一致性與標準化', score: 2.0, category: 'danger' },
    { name: '錯誤防範', score: 2.5, category: 'warning' },
    { name: '辨識勝於回憶', score: 2.5, category: 'warning' },
    { name: '使用彈性與效率', score: 3.0, category: 'warning' },
    { name: '美學與極簡設計', score: 2.0, category: 'danger' },
    { name: '辨識、診斷與復原', score: 3.5, category: 'good' },
    { name: '協助與說明文件', score: 1.0, category: 'danger' },
  ]

  const personas = [
    { id: 'P1', age: '58', role: '退休行政人員', stars: '★☆☆☆☆', rep: '完全新手（數位小白）' },
    { id: 'P2', age: '42', role: '企業主管', stars: '★★☆☆☆', rep: '家中網路的主要「管理者」' },
    { id: 'P3', age: '65', role: '退休教師', stars: '★★☆☆☆', rep: '樂齡長輩，擔心弄壞東西' },
    {
      id: 'P4',
      age: '35',
      role: '行銷專員',
      stars: '★★★☆☆',
      rep: '一般上班族，熟悉但不具技術背景',
    },
    { id: 'P5', age: '45', role: '自由工作者', stars: '★★☆☆☆', rep: '智慧家庭玩家，著重裝置' },
    { id: 'P6', age: '30', role: '軟體工程師', stars: '★★★★★', rep: '重度使用者（對照組）' },
  ]

  /* 03 卡片分類 — 修正前：現有系統架構（I-040GW Router 模式） */
  const cardSortingBefore = [
    { cat: '快速設定', items: ['網際網路快速設定', '無線快速設定', '快速設定精靈'] },
    { cat: '系統狀態', items: ['PON 狀態', '連線測試', '硬體／軟體版本資訊'] },
    {
      cat: '進階設定',
      items: [
        'LAN 設定',
        'WAN 設定',
        'DHCP 租借時間',
        'TR069 管理',
        'SNMP',
        'Registration ID/SLID',
      ],
    },
    { cat: 'Wi-Fi 設定', items: ['Wi-Fi 11n 加密設定', '無線基本設定', 'WPS'] },
    { cat: '維護功能', items: ['韌體更新', '設定檔備份／還原', '重新啟動', '回復出廠預設值'] },
    {
      cat: 'Firewall 設定',
      items: [
        'Layer2 Filter',
        'Layer3 Filter (IPv4)',
        'Layer3 Filter (IPv6)',
        'Port Forwarding',
        'MCL (IPv4/IPv6)',
      ],
    },
    { cat: 'CFM', items: ['（工程維護專用模組，一般使用者不會用到）'] },
  ]

  /* 03 卡片分類 — 修正後：以使用者心智模型重組 */
  const cardSortingAfter = [
    { cat: '首頁總覽', items: ['系統狀態摘要', '連線狀態'] },
    { cat: '網路連線', items: ['WAN 連線設定', '網路連線診斷'] },
    { cat: 'Wi-Fi', items: ['Wi-Fi 名稱與密碼', '無線安全設定'] },
    { cat: '我家的裝置', items: ['已連線裝置清單（有線＋無線統一）'] },
    { cat: '安全與防護', items: ['裝置封鎖／黑名單設定', '允許／封鎖裝置清單'] },
    { cat: '系統維護', items: ['韌體更新', '設定檔備份／還原', '重新啟動', '回復出廠預設值'] },
    {
      cat: '進階／專業技術',
      items: ['獨立隔離區（需警示）— 保留 TR069／SNMP／Port Forwarding 等'],
    },
  ]

  /* 08 選單重新命名 — 任務對照表 */
  const relabelingData = [
    {
      task: 'T4',
      round2: 'DHCP 租借時間設定／連接埠 MAC 位址資訊',
      round3: '連線裝置清單（新增，統一顯示有線＋無線）',
    },
    {
      task: 'T6',
      round2: 'PON 連線狀態／系統資訊',
      round3: '網路連線診斷',
    },
    {
      task: 'T7',
      round2: 'Layer2/3 Filter',
      round3: '裝置封鎖／黑名單設定（Layer2/3 Filter）',
    },
  ]

  /* 10 項樹狀測試固定任務題目（三輪對照測試使用同一組問題） */
  const testTasks = [
    {
      num: '1',
      scenario: '你想要重新啟動路由器。請找出可以重新啟動的地方。',
      legacy: '維護功能',
      answer: '重新啟動',
      r1: '100%',
      r2: '100%',
      r3: '100%',
    },
    {
      num: '2',
      scenario: '你想要更改 Wi-Fi 密碼。請找出可以設定的地方。',
      legacy: 'Wi-Fi 設定',
      answer: 'Wi-Fi 11n 加密設定',
      r1: '100%',
      r2: '100%',
      r3: '100%',
    },
    {
      num: '3',
      scenario: '你懷疑路由器的韌體過舊，想檢查並更新。請找出相關設定。',
      legacy: '維護功能',
      answer: '韌體更新',
      r1: '100%',
      r2: '100%',
      r3: '100%',
    },
    {
      num: '4',
      scenario: '你想要查看目前有哪些裝置連上你的家庭網路（有線或無線）。請找出可以查看的地方。',
      legacy: '系統狀態',
      answer: 'DHCP 租借時間／連接埠 MAC 資訊／Wi-Fi 客戶端資訊',
      r1: '100%',
      r2: '50.0%',
      r3: '100%',
    },
    {
      num: '5',
      scenario: '你想要把路由器恢復原廠設定。請找出可以操作的地方。',
      legacy: '維護功能',
      answer: '回復出廠預設值',
      r1: '100%',
      r2: '100%',
      r3: '100%',
    },
    {
      num: '6',
      scenario: '你的網路突然斷線，想確認是不是路由器本身的問題。請找出可以檢查連線狀態的地方。',
      legacy: '系統狀態',
      answer: '告警狀態／連接測試',
      r1: '33.3%',
      r2: '33.3%',
      r3: '100%',
    },
    {
      num: '7',
      scenario: '你想要封鎖特定裝置連上你的家庭網路。請找出可以設定的地方。',
      legacy: 'Firewall 設定',
      answer: 'Layer2 Filter／Layer3 Filter (IPv4)／Layer3 Filter (IPv6)／Port Forwarding',
      r1: '16.7%',
      r2: '16.7%',
      r3: '100%',
    },
    {
      num: '8',
      scenario:
        '你想要確認路由器是否有任何低階、工程師導向的設定，通常由 ISP 或技術人員設定。這些通常會在哪裡？（選取任一子項目即算成功。）',
      legacy: '進階設定',
      answer: '進階設定（任一子項目，例如 TR069、SNMP、Registration ID/SLID）',
      r1: '16.7%',
      r2: '83.3%',
      r3: '100%',
    },
    {
      num: '9',
      scenario: '你的 ISP 要求你更改裝置連上網際網路的方式。請找出可以操作的地方。',
      legacy: '進階設定',
      answer: 'WAN 連線設定',
      r1: '100%',
      r2: '100%',
      r3: '100%',
    },
    {
      num: '10',
      scenario: '你擔心設定會被不小心更改，想先備份，以便之後可以還原。請找出可以操作的地方。',
      legacy: '維護功能',
      answer: '設定檔備份',
      r1: '100%',
      r2: '100%',
      r3: '100%',
    },
  ]

  return (
    <div
      className="case-study-root"
      style={{ backgroundColor: '#ffffff', color: '#111827', width: '100%', overflowX: 'hidden' }}
    >
      <style>{`
        .router-persona-desktop { display: grid; }
        .router-persona-mobile { display: none; }
        .router-phase2-desktop { display: block; }
        .router-phase2-mobile { display: none; }
        @media (max-width: 768px) {
          .router-persona-desktop { display: none !important; }
          .router-persona-mobile { display: block !important; }
          .router-phase2-desktop { display: none !important; }
          .router-phase2-mobile { display: flex !important; }
          .case-study-root section {
            padding: 0 1.25rem !important;
            margin-top: 3.5rem !important;
            margin-bottom: 3.5rem !important;
          }
          .case-study-root section.router-research-section,
          .case-study-root section.router-insight-section,
          .case-study-root section.router-mockups-section {
            padding: 4rem 1.25rem 3.5rem !important;
            margin-top: 3.5rem !important;
            margin-bottom: 3.5rem !important;
          }
          .case-study-root section.router-conclusion-section {
            padding: 4rem 1.25rem 3.5rem !important;
            margin-top: 3.5rem !important;
            margin-bottom: 0 !important;
          }
          .case-study-root h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
          }
          .case-study-root p {
            font-size: 0.95rem !important;
            line-height: 1.7 !important;
          }
          .router-hero-container {
            padding: 8.5rem 1.25rem 11rem !important;
            min-height: 640px !important;
          }
          .router-hero-container h1 {
            font-size: clamp(1.6rem, 5.5vw, 2.2rem) !important;
          }
          .router-hero-container p {
            font-size: 0.95rem !important;
            margin-bottom: 2rem !important;
          }
          .router-hero-stats {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.75rem 1.25rem !important;
            margin-top: 2.5rem !important;
            margin-left: auto !important;
            margin-right: auto !important;
            max-width: 480px !important;
            width: 100% !important;
          }
          .router-hero-stat {
            flex: none !important;
            text-align: center !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .router-hero-stat-divider {
            display: none !important;
          }
          .router-hero-meta-box {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
            padding: 1.15rem 1rem !important;
            margin-top: 2.5rem !important;
          }
          .router-hero-meta-box > div:last-child {
            grid-column: span 2 !important;
          }
          .router-persona-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
          }
          .router-persona-grid > div {
            padding: 1.1rem 0.85rem !important;
            gap: 0.75rem !important;
            border-radius: 16px !important;
          }
          .router-persona-grid > div > div:first-child {
            gap: 0.65rem !important;
          }
          .router-persona-grid > div > div:first-child > div:first-child {
            width: 42px !important;
            height: 42px !important;
            font-size: 0.95rem !important;
          }
          .router-persona-grid > div > div:first-child > div:last-child > div:first-child {
            font-size: 0.95rem !important;
          }
          .router-persona-grid > div > div:first-child > div:last-child > div:last-child {
            font-size: 0.75rem !important;
          }
          .router-table-wrap {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .router-table-wrap table {
            min-width: 600px !important;
          }
          .wifi-demo-container {
            aspect-ratio: auto !important;
            min-height: auto !important;
            height: auto !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .wifi-demo-container > div:last-child {
            flex-direction: column !important;
            height: auto !important;
          }
          .wifi-demo-container aside {
            width: 100% !important;
            border-right: none !important;
            border-bottom: 1px solid #e2e8f0 !important;
          }
          .wifi-demo-container main {
            overflow: visible !important;
          }
          .router-ai-sim-card {
            background-color: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            padding: 1.5rem 0 0 !important;
            margin-bottom: 2rem !important;
          }
          .router-ai-sim-card h3 {
            font-size: 1.15rem !important;
          }
          .router-ai-sim-card > div:last-child {
            border-radius: 12px !important;
            width: 100% !important;
          }
          #process-at-a-glance > div {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
            width: 100% !important;
          }
          #process-at-a-glance > div > div {
            padding: 1.1rem 0.85rem !important;
            border-radius: 14px !important;
            box-sizing: border-box !important;
          }
          #process-at-a-glance > div > div > div:first-child > div:nth-child(2) {
            font-size: 0.95rem !important;
            line-height: 1.35 !important;
          }
          .router-legacy-tree {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.75rem !important;
          }
          .router-validation-stats {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.85rem !important;
          }
          .router-validation-stats > div {
            padding: 1.15rem 0.85rem !important;
            text-align: center !important;
            border-radius: 14px !important;
          }
          .router-validation-stats > div > div:first-child {
            font-size: clamp(1.8rem, 6vw, 2.3rem) !important;
            margin-bottom: 0.4rem !important;
          }
          .router-validation-stats > div > div:nth-child(2) {
            font-size: 0.85rem !important;
            line-height: 1.35 !important;
          }
          .router-validation-stats > div > p {
            font-size: 0.75rem !important;
            line-height: 1.4 !important;
            margin-top: 0.25rem !important;
          }
          .router-validation-stats > div:last-child {
            grid-column: span 2 !important;
            max-width: 320px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
          .router-insight-section {
            padding: 3.5rem 1.25rem !important;
          }
          .router-insight-section > div {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .router-phase1-grid {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            max-width: 100% !important;
            gap: 1.25rem !important;
            box-sizing: border-box !important;
          }
          .router-phase1-card,
          .router-phase2-card {
            padding: 1.25rem 0.85rem !important;
            box-sizing: border-box !important;
            width: 100% !important;
            max-width: 100% !important;
            border-radius: 16px !important;
            overflow: hidden !important;
          }
          .router-phase1-card > div:first-child {
            flex-wrap: wrap !important;
            gap: 0.5rem !important;
          }
          .router-phase1-card > div:last-child > div {
            padding: 0.85rem 0.75rem !important;
          }
          .router-phase2-card table {
            min-width: 500px !important;
          }
          .router-insight-section div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
          }
          .router-insight-section div[style*="padding: 2rem 2.25rem"],
          .router-insight-section div[style*="padding: '2rem 2.25rem'"] {
            padding: 1.25rem 1rem !important;
          }
          .router-insight-section div[style*="padding"] {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .router-phase-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
          .router-phase-header h3 {
            font-size: 1.15rem !important;
            line-height: 1.4 !important;
          }
          .router-legacy-card {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin-bottom: 2rem !important;
          }
          .router-legacy-tree > div {
            padding: 0.75rem 0.65rem !important;
          }
          .router-legacy-tree > div:last-child {
            grid-column: span 2 !important;
            max-width: 280px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
        }
      `}</style>
      {/* 1. Integrated Hero Header with Deep Dark Obsidian Background & Router Image Overlay */}
      <div
        className="router-hero-container"
        style={{
          position: 'relative',
          width: '100%',
          padding: '10rem 2rem 12rem',
          minHeight: 'min(880px, 92vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          overflow: 'hidden',
          background:
            'linear-gradient(180deg, #0a0a0d 0%, #131318 50%, #1f1f28 82%, #4a4a58 93%, #ffffff 100%)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/images/router-hero-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.7,
            mixBlendMode: 'luminosity',
            pointerEvents: 'none',
          }}
        />
        {/* Slightly Darker Soft Fade Overlay to White */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(10, 10, 13, 0.68) 0%, rgba(19, 19, 24, 0.62) 60%, rgba(31, 31, 40, 0.46) 85%, rgba(255, 255, 255, 0.9) 97%, #ffffff 100%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            maxWidth: '1250px',
            margin: '0 auto',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h1
            style={{
              fontSize: 'clamp(2.25rem, 4vw, 3.25rem)',
              fontWeight: 800,
              color: '#ffffff',
              lineHeight: 1.25,
              marginBottom: '1.25rem',
              letterSpacing: '-0.02em',
            }}
          >
            中華電信路由器管理介面優化
          </h1>

          <p
            style={{
              fontSize: '1.1rem',
              color: '#94a3b8',
              lineHeight: 1.7,
              maxWidth: '960px',
              margin: '0 auto 3rem',
            }}
          >
            重新設計家用閘道器的網頁管理體驗，改善網路設定流程，降低複雜功能的理解與操作門檻，讓一般使用者能更直覺地完成設定。
          </p>

          {/* 4 Impact Stats Row with Vertical Dividers (Temple Case Study Style) */}
          <div
            className="router-hero-stats"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '1.5rem',
              marginTop: '3rem',
              maxWidth: '1160px',
              margin: '0 auto',
            }}
          >
            <div className="router-hero-stat" style={{ textAlign: 'left', flex: '1 1 180px' }}>
              <div
                style={{
                  color: '#4ade80',
                  fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  marginBottom: '0.4rem',
                  letterSpacing: '-0.01em',
                }}
              >
                100%
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.875rem' }}>
                最終任務成功率
              </div>
              <div style={{ fontSize: '0.775rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                Round 3 樹狀測試
              </div>
            </div>

            <div
              className="router-hero-stat-divider"
              style={{
                width: '1px',
                height: '42px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                alignSelf: 'center',
              }}
            />

            <div className="router-hero-stat" style={{ textAlign: 'left', flex: '1 1 180px' }}>
              <div
                style={{
                  color: '#4ade80',
                  fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  marginBottom: '0.4rem',
                  letterSpacing: '-0.01em',
                }}
              >
                +23.3%
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.875rem' }}>
                成功率相對提升
              </div>
              <div style={{ fontSize: '0.775rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                相較舊架構 76.7%
              </div>
            </div>

            <div
              className="router-hero-stat-divider"
              style={{
                width: '1px',
                height: '42px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                alignSelf: 'center',
              }}
            />

            <div className="router-hero-stat" style={{ textAlign: 'left', flex: '1 1 180px' }}>
              <div
                style={{
                  color: '#4ade80',
                  fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  marginBottom: '0.4rem',
                  letterSpacing: '-0.01em',
                }}
              >
                10 / 10
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.875rem' }}>
                任務全數通過
              </div>
              <div style={{ fontSize: '0.775rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                達成 100% 成功率
              </div>
            </div>

            <div
              className="router-hero-stat-divider"
              style={{
                width: '1px',
                height: '42px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                alignSelf: 'center',
              }}
            />

            <div className="router-hero-stat" style={{ textAlign: 'left', flex: '1 1 180px' }}>
              <div
                style={{
                  color: '#fb923c',
                  fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  marginBottom: '0.4rem',
                  letterSpacing: '-0.01em',
                }}
              >
                -10.8s
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.875rem' }}>
                平均耗時縮短
              </div>
              <div style={{ fontSize: '0.775rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                由 33.3s 下降至 22.5s
              </div>
            </div>
          </div>

          {/* PROJECT METADATA — Integrated Dark Glassmorphism Box (Matching Temple & Healing Herbar) */}
          <div
            className="router-hero-meta-box"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
              padding: '1.5rem 2rem',
              marginTop: '3.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.5rem',
              textAlign: 'left',
              maxWidth: '1160px',
              margin: '3.5rem auto 0',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  color: '#818cf8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.35rem',
                }}
              >
                專案角色
              </div>
              <div
                style={{
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  lineHeight: 1.4,
                }}
              >
                UI/UX 設計師
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  color: '#818cf8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.35rem',
                }}
              >
                專案時間
              </div>
              <div
                style={{
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  lineHeight: 1.4,
                }}
              >
                2026.07
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  color: '#818cf8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.35rem',
                }}
              >
                研究範疇
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.4rem',
                  marginTop: '0.2rem',
                }}
              >
                {['Product Design', 'UX Research', 'UI Design'].map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#e2e8f0',
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      padding: '4px 12px',
                      borderRadius: '99px',
                      display: 'inline-block',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Problem / Context Section */}
      <section style={{ maxWidth: '1200px', margin: '3.5rem auto 6rem', padding: '0 2rem' }}>
        <h2 style={styles.sectionHeading}>問題與背景</h2>
        <p style={{ ...styles.paragraphLarge, marginBottom: '2rem' }}>
          中華電信路由器管理介面傳統的選單邏輯，是完全照著工程師熟悉的後端系統架構分類：如{' '}
          <code
            style={{
              fontFamily: 'monospace',
              backgroundColor: '#f1f5f9',
              color: '#334155',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
            }}
          >
            DHCP
          </code>
          、
          <code
            style={{
              fontFamily: 'monospace',
              backgroundColor: '#f1f5f9',
              color: '#334155',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
            }}
          >
            PON
          </code>
          、
          <code
            style={{
              fontFamily: 'monospace',
              backgroundColor: '#f1f5f9',
              color: '#334155',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
            }}
          >
            Layer2/3 篩選
          </code>
          。這種「工程視角 vs 用戶任務視角」的落差，導致非技術背景的使用者連最簡單的「更改 Wi-Fi
          密碼」或「查看連線裝置」都必須撥打客服電話求解。
        </p>

        {/* Visual Legacy IA Diagram Card / Asset Slot */}
        <div
          className="router-legacy-card"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '2rem',
            marginBottom: '2.5rem',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.04)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
              舊版工程架構樹狀圖（7 大系統模組）
            </div>
          </div>

          {/* Interactive Visual IA Tree representation */}
          <div
            className="router-legacy-tree"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem',
            }}
          >
            {cardSortingBefore.map((cat, idx) => (
              <div
                key={idx}
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  padding: '1rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: '#991b1b',
                    textTransform: 'uppercase',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#ef4444',
                    }}
                  />
                  {cat.cat}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {cat.items.slice(0, 3).map((item, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: '0.8rem',
                        color: '#475569',
                        backgroundColor: '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {item}
                    </div>
                  ))}
                  {cat.items.length > 3 && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>
                      +{cat.items.length - 3} 項...
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* HMW Box */}
        <div
          style={{
            borderLeft: '4px solid #4f46e5',
            backgroundColor: '#f8fafc',
            borderRadius: '0 16px 16px 0',
            padding: '2rem 2.5rem',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 800,
              color: '#4f46e5',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '0.75rem',
              fontFamily: 'var(--font-sans)',
            }}
          >
            HOW MIGHT WE
          </div>
          <div
            style={{
              fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif',
              fontStyle: 'italic',
              fontSize: 'clamp(20px, 2.5vw, 23px)',
              fontWeight: 600,
              color: '#0f172a',
              lineHeight: 1.6,
            }}
          >
            讓同一套介面，在不刪減任何功能的前提下，同時滿足技術人員與一般使用者的需求？
          </div>
        </div>
      </section>

      {/* 3. Research Approach Section — Research Methods & Personas & Design Process */}
      <section
        className="router-research-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#f1f5f9',
          padding: '6rem 2rem',
          marginBottom: '6rem',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={styles.sectionHeading}>研究方法與流程</h2>

          {/* 簡化版流程總覽區塊 (Process at a Glance - Modern Refined Step Cards) */}
          <div id="process-at-a-glance" style={{ marginBottom: '4.5rem', marginTop: '2rem' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '1.25rem',
                alignItems: 'stretch',
                width: '100%',
              }}
            >
              {[
                {
                  step: '01',
                  title: '10 項互動設計的通用經驗法則評估',
                  desc: '盤點架構、評估可用性問題',
                  accentColor: '#6366f1',
                },
                {
                  step: '02',
                  title: '定義使用者',
                  desc: '6位persona，涵蓋新手到工程師',
                  accentColor: '#3b82f6',
                },
                {
                  step: '03',
                  title: '樹狀測試與架構迭代',
                  desc: '舊版基準、架構重組至語意用字驗證',
                  subBadges: ['Round 1 基準', 'Round 2 重組', 'Round 3 語意'],
                  accentColor: '#8b5cf6',
                },
                {
                  step: '04',
                  title: 'UI Redesign Mockups',
                  desc: '設計3個優先功能的視覺稿',
                  accentColor: '#10b981',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderTop: `4px solid ${item.accentColor}`,
                    borderRadius: '16px',
                    padding: '1.5rem 1.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    boxShadow: '0 4px 15px rgba(15, 23, 42, 0.04)',
                    position: 'relative',
                  }}
                >
                  <div>
                    {/* Top Row: Step Tag + Subtle Serial Number */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.85rem',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          color: item.accentColor,
                          backgroundColor: `${item.accentColor}12`,
                          border: `1px solid ${item.accentColor}30`,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          letterSpacing: '0.04em',
                        }}
                      >
                        STEP {item.step}
                      </span>
                      <span
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 900,
                          color: '#cbd5e1',
                          lineHeight: 1,
                        }}
                      >
                        {item.step}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: '1.02rem',
                        fontWeight: 800,
                        color: '#0f172a',
                        marginBottom: '0.45rem',
                        lineHeight: 1.35,
                      }}
                    >
                      {item.title}
                    </div>

                    <div
                      style={{
                        fontSize: '0.85rem',
                        color: '#64748b',
                        margin: 0,
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.desc}
                    </div>
                  </div>

                  {item.subBadges && (
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '0.2rem' }}
                    >
                      {item.subBadges.map((badge, bIdx) => (
                        <span
                          key={bIdx}
                          style={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: '#6366f1',
                            backgroundColor: '#eef2ff',
                            border: '1px solid #c7d2fe',
                            padding: '1px 6px',
                            borderRadius: '6px',
                          }}
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 10 項互動設計的通用經驗法則評估 (10 Heuristic Evaluation) */}
          <div id="heuristic-evaluation" style={{ marginBottom: '5rem', scrollMarginTop: '6rem' }}>
            <h3
              style={{
                fontSize: '1.4rem',
                fontWeight: 800,
                color: '#0f172a',
                marginBottom: '1rem',
              }}
            >
              10 項互動設計的通用經驗法則評估
            </h3>

            {/* 簡短導言 */}
            <p
              style={{
                fontSize: '1.05rem',
                color: '#475569',
                lineHeight: 1.7,
                marginBottom: '2rem',
              }}
            >
              我依 Jakob Nielsen 的十大互動設計法則，逐一檢視這款光世代路由器後台的 26
              個頁面，平均分數為 2.65 / 5。
            </p>

            {/* 長條圖與平均分數卡片 */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '2rem',
                marginBottom: '2.5rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
              }}
            >
              {/* Header with Average Score Badge */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '2rem',
                  paddingBottom: '1.25rem',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a' }}>
                  經驗法則評分總覽 (滿分 5.0)
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    padding: '6px 16px',
                    borderRadius: '99px',
                  }}
                >
                  <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                    平均分數：
                  </span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#2563eb' }}>
                    2.65
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>/ 5.0</span>
                </div>
              </div>

              {/* Horizontal Bar Chart (Flat, Fine Border, Responsive SVG/HTML Bars) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                {heuristicScores.map((item, idx) => {
                  // High-end modern UI palette (Emerald, Warm Amber, Rose Red)
                  const textColor =
                    item.score >= 3.5 ? '#059669' : item.score >= 2.5 ? '#d97706' : '#e11d48'
                  const barGradient =
                    item.score >= 3.5
                      ? 'linear-gradient(90deg, #34d399 0%, #10b981 100%)'
                      : item.score >= 2.5
                        ? 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                        : 'linear-gradient(90deg, #fb7185 0%, #f43f5e 100%)'
                  const percentage = (item.score / 5.0) * 100

                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(160px, 220px) 1fr 50px',
                        alignItems: 'center',
                        gap: '1.25rem',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          color: '#334155',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={item.name}
                      >
                        {item.name}
                      </span>
                      <div
                        style={{
                          height: '14px',
                          backgroundColor: '#f1f5f9',
                          borderRadius: '99px',
                          overflow: 'hidden',
                          position: 'relative',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <div
                          style={{
                            width: `${percentage}%`,
                            height: '100%',
                            background: barGradient,
                            borderRadius: '99px',
                            transition: 'width 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: 800,
                          color: textColor,
                          textAlign: 'right',
                        }}
                      >
                        {item.score.toFixed(1)}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Chart Legend */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '1.5rem',
                  marginTop: '1.75rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #f1f5f9',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#64748b',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                    }}
                  />
                  <span>≥ 3.5 分 (良好)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: '#f59e0b',
                    }}
                  />
                  <span>2.5~3.4 分 (尚可)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: '#f43f5e',
                    }}
                  />
                  <span>&lt; 2.5 分 (需改進)</span>
                </div>
              </div>
            </div>

            {/* 表現最好 (Top 3) vs 表現最弱 (Weakest 3) 兩欄區塊 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2.5rem',
              }}
            >
              {/* Top 3 Card */}
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #bbf7d0',
                  borderRadius: '16px',
                  padding: '1.75rem',
                  boxShadow: '0 4px 15px rgba(34, 197, 94, 0.04)',
                }}
              >
                <div
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    color: '#15803d',
                    marginBottom: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <CheckCircle size={20} color="#16a34a" />
                  <span>表現最好的三項</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div
                    style={{
                      backgroundColor: '#f0fdf4',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      border: '1px solid #dcfce7',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#166534', fontSize: '0.95rem' }}>
                      系統狀態的可見性 (4.0)
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#365314',
                        marginTop: '0.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      每個技術指標都攤在眼前，PON 狀態、流量、告警一應俱全
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: '#f0fdf4',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      border: '1px solid #dcfce7',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#166534', fontSize: '0.95rem' }}>
                      使用者控制與自由 (3.5)
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#365314',
                        marginTop: '0.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      提供多層級的復原機制
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: '#f0fdf4',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      border: '1px solid #dcfce7',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#166534', fontSize: '0.95rem' }}>
                      辨識、診斷與復原 (3.5)
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#365314',
                        marginTop: '0.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      故障排查資訊算直觀
                    </div>
                  </div>
                </div>
              </div>

              {/* Weakest 3 Card */}
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #fecaca',
                  borderRadius: '16px',
                  padding: '1.75rem',
                  boxShadow: '0 4px 15px rgba(239, 68, 68, 0.04)',
                }}
              >
                <div
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    color: '#b91c1c',
                    marginBottom: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <AlertCircle size={20} color="#dc2626" />
                  <span>表現最弱的三項</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div
                    style={{
                      backgroundColor: '#fef2f2',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      border: '1px solid #fee2e2',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#991b1b', fontSize: '0.95rem' }}>
                      協助與說明文件 (1.0)
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#7f1d1d',
                        marginTop: '0.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      全系統找不到一個「說明」按鈕，使用者只能自己摸索
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: '#fef2f2',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      border: '1px solid #fee2e2',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#991b1b', fontSize: '0.95rem' }}>
                      一致性與標準化 (2.0)
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#7f1d1d',
                        marginTop: '0.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      中英夾雜、元件規則不統一
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: '#fef2f2',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      border: '1px solid #fee2e2',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#991b1b', fontSize: '0.95rem' }}>
                      美學與極簡設計 (2.0)
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#7f1d1d',
                        marginTop: '0.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      排版擁擠、視覺老舊
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 一句話總結 (Insight Convergence Callout without Circle Border) */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                marginTop: '3.5rem',
                marginBottom: '1rem',
                position: 'relative',
              }}
            >
              {/* Clean Downward Arrow without Circle Border */}
              <div
                style={{
                  color: '#6366f1',
                  marginBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ArrowDown size={24} strokeWidth={2.5} />
              </div>

              {/* Bold Converged Statement */}
              <div
                style={{
                  color: '#0f172a',
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  lineHeight: 1.6,
                  width: '100%',
                  letterSpacing: '-0.01em',
                  paddingBottom: '2.5rem',
                  marginBottom: '1.5rem',
                  borderBottom: '1px solid #cbd5e1',
                }}
              >
                現行系統設計最大的問題，是介面把工程師才懂的複雜度，原封不動地丟給了一般使用者。
              </div>
            </div>
          </div>

          {/* 6 Visual Persona Cards with Avatar Slots */}
          <div id="personas-spectrum" style={{ marginBottom: '4rem', scrollMarginTop: '6rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  margin: '0 0 0.5rem',
                }}
              >
                定義使用者
              </h3>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem', lineHeight: 1.6 }}>
                為了確保後續資訊架構（IA）能涵蓋多樣化的使用者心智模型，本次建立了 6
                位橫跨「完全數位新手」到「專業軟體工程師」的 Persona
                人物誌，作為樹狀測試與語意白話說明的代表受試對象：
              </p>
            </div>

            {/* Desktop View: Grid Cards */}
            <div
              className="router-persona-grid router-persona-desktop"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '1.5rem',
              }}
            >
              {personas.map((p) => (
                <div
                  key={p.id}
                  style={{
                    backgroundColor: p.id === 'P6' ? '#faf5ff' : '#ffffff',
                    border: p.id === 'P6' ? '1.5px solid #c084fc' : '1px solid #e2e8f0',
                    borderRadius: '20px',
                    padding: '1.75rem',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.03)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                    {/* Avatar Circle Placeholder / Visual */}
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: p.id === 'P6' ? '#f3e8ff' : '#eef2ff',
                        border: p.id === 'P6' ? '2px solid #a855f7' : '2px solid #6366f1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        color: p.id === 'P6' ? '#7e22ce' : '#4f46e5',
                        fontSize: '1.1rem',
                        flexShrink: 0,
                      }}
                    >
                      {p.id}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 800,
                          color: p.id === 'P6' ? '#7e22ce' : '#0f172a',
                          fontSize: '1.15rem',
                        }}
                      >
                        {p.role}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                        {p.age} 歲
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: p.id === 'P6' ? '#f3e8ff' : '#f8fafc',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>
                      技術熟悉度 (Tech Comfort)
                    </span>
                    <span style={{ fontSize: '0.9rem', color: '#d97706', letterSpacing: '2px' }}>
                      {p.stars}
                    </span>
                  </div>

                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                    {p.rep}
                  </p>
                </div>
              ))}
            </div>

            {/* Mobile View: Clean Mobile Table */}
            <div
              className="router-persona-mobile"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th
                      style={{
                        padding: '0.75rem 0.85rem',
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        color: '#475569',
                      }}
                    >
                      ID / 角色
                    </th>
                    <th
                      style={{
                        padding: '0.75rem 0.85rem',
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        color: '#475569',
                      }}
                    >
                      熟悉度
                    </th>
                    <th
                      style={{
                        padding: '0.75rem 0.85rem',
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        color: '#475569',
                      }}
                    >
                      受試特徵與心智模型
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {personas.map((p, idx) => (
                    <tr
                      key={p.id}
                      style={{
                        borderBottom: idx !== personas.length - 1 ? '1px solid #f1f5f9' : 'none',
                        backgroundColor: p.id === 'P6' ? '#faf5ff' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '0.85rem', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 800,
                              color: p.id === 'P6' ? '#7e22ce' : '#4f46e5',
                              backgroundColor: p.id === 'P6' ? '#f3e8ff' : '#eef2ff',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }}
                          >
                            {p.id}
                          </span>
                          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>
                            {p.role}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                          {p.age} 歲
                        </div>
                      </td>
                      <td
                        style={{ padding: '0.85rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}
                      >
                        <span
                          style={{ fontSize: '0.75rem', color: '#d97706', letterSpacing: '1px' }}
                        >
                          {p.stars}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '0.85rem',
                          verticalAlign: 'top',
                          fontSize: '0.8rem',
                          color: '#334155',
                          lineHeight: 1.5,
                        }}
                      >
                        {p.rep}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* 10 Tree Testing Tasks Table */}
          <div id="tree-testing-table" style={{ marginTop: '4rem', scrollMarginTop: '6rem' }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  margin: '0 0 0.5rem',
                }}
              >
                樹狀測試 10 項標準任務題目（三輪測試統一基準）
              </h3>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem', lineHeight: 1.6 }}>
                為了客觀評估與對照舊版架構、新版重組架構與雙軌命名架構的尋路效率，設計了 10
                項代表家用路由器常見日常情境的標準任務（Tasks），在三輪 Tree Testing
                中保持題目一致，以精準追蹤成功率與尋路路徑的改善：
              </p>
            </div>

            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.04)',
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    textAlign: 'left',
                    fontSize: '0.95rem',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>測試任務情境（10 題核心題目）</th>
                      <th style={styles.th}>舊架構對應位置</th>
                      <th style={styles.th}>正確解法答案</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testTasks.map((task, idx) => (
                      <tr
                        key={task.num}
                        style={{
                          borderBottom: idx < testTasks.length - 1 ? '1px solid #f1f5f9' : 'none',
                          backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                        }}
                      >
                        <td
                          style={{
                            ...styles.td,
                            fontWeight: 800,
                            color: '#4f46e5',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          T{task.num}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            color: '#1e293b',
                            minWidth: '280px',
                            lineHeight: 1.5,
                          }}
                        >
                          {task.scenario}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            fontWeight: 600,
                            color: '#64748b',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {task.legacy}
                        </td>
                        <td style={{ ...styles.td, color: '#334155', minWidth: '240px' }}>
                          {task.answer}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: AI-Assisted UX Validation Section */}
      <section style={{ maxWidth: '1200px', margin: '0 auto 6rem', padding: '0 2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2
            style={{
              fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
              fontWeight: 800,
              color: '#0f172a',
              margin: '0 0 1rem',
            }}
          >
            AI 輔助用戶體驗驗證
          </h2>

          <p
            style={{
              fontSize: '1rem',
              color: '#64748b',
              maxWidth: '820px',
              margin: '0 auto',
              lineHeight: 1.7,
            }}
          >
            在設計過程中，我利用 AI 模擬不同 Persona
            的使用情境，測試任務流程與互動設計，快速發現可能的使用阻礙與邊界案例，並根據結果持續優化設計。
          </p>
        </div>

        {/* Demo Video Embedding Card */}
        <div
          className="router-ai-sim-card"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '24px',
            padding: '2rem',
            boxShadow: '0 20px 30px -10px rgba(0, 0, 0, 0.05)',
            maxWidth: '960px',
            margin: '0 auto',
          }}
        >
          {/* 16:9 Video/Image Frame */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0',
              marginBottom: '1.25rem',
            }}
          >
            <img
              src="/images/persona_01_test_1785022934117.webp"
              alt="AI Persona Simulation Test"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
              }}
            />
          </div>

          <div
            style={{
              textAlign: 'center',
              marginTop: '1.25rem',
            }}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: 800,
                color: '#0f172a',
                margin: '0 0 0.4rem',
              }}
            >
              AI 用戶模擬測試
            </h3>
            <p style={{ margin: 0, fontSize: '0.92rem', color: '#475569', lineHeight: 1.6 }}>
              此影片展示 AI 如何模擬不同使用者角色，進行任務流程測試與互動驗證。
            </p>
          </div>
        </div>
      </section>

      {/* 4. Key Insight — Focal IA Before/After & Naming Strategy (Maximum Weight) */}
      <section
        id="ia-reconstruction"
        className="router-insight-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#0f172a',
          color: '#ffffff',
          padding: '6rem 2rem',
          marginBottom: '6rem',
          scrollMarginTop: '4rem',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Subtitle explaining 2-stage verification */}
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <h2 style={{ ...styles.sectionHeading, color: '#ffffff' }}>
              核心洞察：資訊架構與命名重構
            </h2>
            <p
              style={{
                fontSize: '1.15rem',
                color: '#cbd5e1',
                maxWidth: '860px',
                margin: '1rem auto 0',
                lineHeight: 1.6,
              }}
            >
              重新分類架構只能解決「找不到大分類」的問題，解決不了「看不懂子選單」的問題。本案透過兩階段對照，獨立驗證「Round
              1~2 架構重組」與「Round 3 語意命名」的核心效益。
            </p>
          </div>

          {/* 階段 1：Round 1 vs Round 2 資訊架構重組 */}
          <div style={{ marginBottom: '4.5rem' }}>
            <div
              className="router-phase-header"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '1.5rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                paddingBottom: '1rem',
              }}
            >
              <span
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  color: '#0f172a',
                  backgroundColor: '#38bdf8',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  letterSpacing: '0.05em',
                  flexShrink: 0,
                }}
              >
                PHASE 1
              </span>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                資訊架構重組 (IA Restructuring) — Round 1 舊基準 vs Round 2 新架構
              </h3>
            </div>

            {/* Side-by-Side Visual IA Before vs After Cards */}
            <div
              className="router-phase1-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))',
                gap: '2rem',
              }}
            >
              {/* BEFORE Card (Round 1) */}
              <div
                className="router-phase1-card"
                style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.9)',
                  border: '1.5px solid #ef4444',
                  borderRadius: '20px',
                  padding: '2rem 2.25rem',
                  boxShadow: '0 10px 30px rgba(239, 68, 68, 0.1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#f87171',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                    }}
                  >
                    <AlertCircle size={22} />
                    <span>修正前 — 工程導向架構</span>
                  </div>
                  <span
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 800,
                      color: '#ffffff',
                      backgroundColor: '#ef4444',
                      padding: '4px 12px',
                      borderRadius: '99px',
                      letterSpacing: '0.03em',
                    }}
                  >
                    Round 1 舊基準
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                  {cardSortingBefore.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.85)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        padding: '1rem 1.25rem',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: '#f87171',
                          textTransform: 'uppercase',
                          fontWeight: 800,
                          marginBottom: '0.3rem',
                        }}
                      >
                        {item.cat}
                      </div>
                      <div
                        style={{
                          fontSize: '0.9rem',
                          color: '#94a3b8',
                          lineHeight: 1.5,
                        }}
                      >
                        {item.items.join(' ・ ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AFTER Card (Round 2) */}
              <div
                className="router-phase1-card"
                style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.9)',
                  border: '1.5px solid #22c55e',
                  borderRadius: '20px',
                  padding: '2rem 2.25rem',
                  boxShadow: '0 10px 30px rgba(34, 197, 94, 0.1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#4ade80',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                    }}
                  >
                    <CheckCircle size={22} />
                    <span>修正後 — 使用者任務導向架構</span>
                  </div>
                  <span
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 800,
                      color: '#ffffff',
                      backgroundColor: '#16a34a',
                      padding: '4px 12px',
                      borderRadius: '99px',
                      letterSpacing: '0.03em',
                    }}
                  >
                    Round 2 重組驗證
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                  {cardSortingAfter.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.85)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        padding: '1rem 1.25rem',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: '#4ade80',
                          textTransform: 'uppercase',
                          fontWeight: 800,
                          marginBottom: '0.3rem',
                        }}
                      >
                        {item.cat}
                      </div>
                      <div
                        style={{
                          fontSize: '0.9rem',
                          color: '#e2e8f0',
                          lineHeight: 1.5,
                        }}
                      >
                        {item.items.join(' ・ ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 階段 2：Round 3 語意雙軌命名策略 */}
          <div>
            <div
              className="router-phase-header"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '1.5rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                paddingBottom: '1rem',
              }}
            >
              <span
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  color: '#ffffff',
                  backgroundColor: '#9333ea',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  letterSpacing: '0.05em',
                  flexShrink: 0,
                }}
              >
                PHASE 2
              </span>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                語意雙軌命名策略 (Dual-Track Naming) — Round 3 白話雙軌修正
              </h3>
            </div>

            {/* Table Card Container */}
            <div
              className="router-phase2-card"
              style={{
                backgroundColor: 'rgba(30, 41, 59, 0.9)',
                border: '1.5px solid #a855f7',
                borderRadius: '20px',
                padding: '2.5rem',
                boxShadow: '0 10px 30px rgba(168, 85, 247, 0.1)',
              }}
            >
              <p
                style={{
                  fontSize: '0.95rem',
                  color: '#cbd5e1',
                  maxWidth: '820px',
                  margin: '0 auto 1.5rem',
                  textAlign: 'center',
                  lineHeight: 1.6,
                }}
              >
                大分類採用任務語言，子選單採用白話中文並在括號內保留原技術術語（Round 3
                修正），兼顧一般使用者與專業工程人員。
              </p>

              {/* Desktop View: Table */}
              <div className="router-phase2-desktop" style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    textAlign: 'left',
                    fontSize: '0.95rem',
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.9)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                      }}
                    >
                      <th style={{ ...styles.th, color: '#94a3b8' }}>任務</th>
                      <th style={{ ...styles.th, color: '#94a3b8' }}>原舊用詞 (Legacy Term)</th>
                      <th style={{ ...styles.th, color: '#94a3b8' }}>
                        新白話雙軌用詞 (Redesigned Term)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {relabelingData.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom:
                            idx < relabelingData.length - 1
                              ? '1px solid rgba(255, 255, 255, 0.08)'
                              : 'none',
                        }}
                      >
                        <td style={{ ...styles.td, fontWeight: 800, color: '#818cf8' }}>
                          {row.task}
                        </td>
                        <td style={{ ...styles.td, color: '#94a3b8' }}>{row.round2}</td>
                        <td style={{ ...styles.td, fontWeight: 700, color: '#4ade80' }}>
                          {row.round3}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View: Vertical Cards Stack */}
              <div
                className="router-phase2-mobile"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}
              >
                {relabelingData.map((row, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: 'rgba(15, 23, 42, 0.75)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      padding: '0.9rem 1rem',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: 800,
                        color: '#818cf8',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {row.task}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.35rem',
                        fontSize: '0.825rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                        }}
                      >
                        <span style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>
                          原舊用詞：
                        </span>
                        <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>
                          {row.round2}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                        }}
                      >
                        <span style={{ color: '#4ade80', fontWeight: 700 }}>{row.round3}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Validation Section — Tree Testing Impact Stats & Bar Chart */}
      <section style={{ maxWidth: '1200px', margin: '0 auto 6rem', padding: '0 2rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
          }}
        >
          <h2 style={styles.sectionHeading}>驗證與成效</h2>
        </div>
        <p style={{ ...styles.paragraph, marginBottom: '3rem' }}>
          透過三輪樹狀測試獨立驗證變數，最終架構與命名達到 100% 任務成功率：
        </p>

        {/* Task Success Rate Visual Bar Chart */}
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '2.5rem',
            marginBottom: '3rem',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.04)',
          }}
        >
          <h3
            style={{
              fontSize: '1.2rem',
              fontWeight: 800,
              color: '#0f172a',
              marginBottom: '1.5rem',
            }}
          >
            三輪樹狀測試任務成功率比較 (Task Success Rate Bar Chart)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {[
              { round: 'Round 1：舊架構基準測試', rate: '76.7%', val: 76.7, color: '#64748b' },
              { round: 'Round 2：新架構（僅重組）', rate: '78.3%', val: 78.3, color: '#38bdf8' },
              { round: 'Round 3：新架構＋雙軌命名', rate: '100.0%', val: 100, color: '#22c55e' },
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#334155',
                  }}
                >
                  <span>{item.round}</span>
                  <span style={{ color: item.color, fontWeight: 800 }}>{item.rate}</span>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '14px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '99px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${item.val}%`,
                      height: '100%',
                      backgroundColor: item.color,
                      borderRadius: '99px',
                      transition: 'width 1s ease-in-out',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Clean Numeric Stat Cards Grid */}
        <div
          className="router-validation-stats"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '2rem',
          }}
        >
          <div style={styles.darkCard}>
            <div
              style={{
                fontSize: '3.5rem',
                fontWeight: 800,
                color: '#34d399',
                lineHeight: 1,
                marginBottom: '0.75rem',
              }}
            >
              100%
            </div>
            <div
              style={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: '0.4rem',
              }}
            >
              最終任務成功率
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
              相較舊架構 76.7% 顯著提升（10/10 任務全數通過）。
            </p>
          </div>

          <div style={styles.darkCard}>
            <div
              style={{
                fontSize: '3.5rem',
                fontWeight: 800,
                color: '#38bdf8',
                lineHeight: 1,
                marginBottom: '0.75rem',
              }}
            >
              -10.8s
            </div>
            <div
              style={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: '0.4rem',
              }}
            >
              平均操作時間縮短
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
              完成任務平均耗時由 33.3 秒下降至 22.5 秒。
            </p>
          </div>

          <div style={styles.darkCard}>
            <div
              style={{
                fontSize: '3.5rem',
                fontWeight: 800,
                color: '#fb923c',
                lineHeight: 1,
                marginBottom: '0.75rem',
              }}
            >
              10 / 10
            </div>
            <div
              style={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: '0.4rem',
              }}
            >
              所有 Personas 全數通過
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
              包含完全新手（P1）與長輩（P3）皆能自主順利操作。
            </p>
          </div>
        </div>
      </section>

      {/* 14. UI Redesign Mockups Section — Desktop 12-Column Responsive Page */}
      <section
        id="ui-redesign-mockups"
        className="router-mockups-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#f8fafc',
          padding: '6rem 2rem',
          marginBottom: '0',
          scrollMarginTop: '4rem',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <h2 style={styles.sectionHeading}>UI Redesign Mockups</h2>
          </div>
          <p style={{ ...styles.paragraph, marginBottom: '2.5rem' }}>
            回到開頭「使用者最常因無法自行完成而撥打客服」的核心問題，Wi-Fi
            是家庭使用者操作頻率最高的功能，卻也是舊系統問題最集中的一頁：說明文件、一致性、視覺設計三項評分全數墊底，以下針對「設定Wi-Fi
            」為例進行功能介面優化設計。
          </p>

          {/* Embedded Full 12-Column Desktop Router Page */}
          <WifiSettingsFullPage />
        </div>
      </section>

      {/* 15. Conclusion — full-bleed dark */}
      <section
        className="router-conclusion-section"
        style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#0f172a',
          color: '#ffffff',
          padding: '6rem 2rem 5rem',
          marginBottom: 0,
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ ...styles.sectionHeading, color: '#ffffff', textAlign: 'center' }}>
            Takeaways
          </h2>

          <p
            style={{
              fontSize: '1.4rem',
              lineHeight: 1.6,
              color: '#ffffff',
              margin: '0 auto 2.5rem',
              maxWidth: '56rem',
              textAlign: 'center',
            }}
          >
            路由器介面的可用性問題，是「資訊架構」與「命名語意」兩層問題的疊加，透過把兩者分開處理，逐一確認每一項修正的實際成效。
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2.5rem',
            }}
          >
            <div style={styles.darkStatCard}>
              <div
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  color: '#fbbf24',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.6rem',
                }}
              >
                Round 2
              </div>
              <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.95rem', lineHeight: 1.7 }}>
                重新分類只解決了一個「放哪裡」的問題（任務 8），卻讓一個原本找得到的任務（任務
                4）變難，另外兩個任務完全沒有進步。
              </p>
            </div>
            <div style={styles.darkStatCard}>
              <div
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  color: '#4ade80',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.6rem',
                }}
              >
                Round 3
              </div>
              <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.95rem', lineHeight: 1.7 }}>
                基於修改後的結構、修改與意命名，使全部任務達到 100% 成功、完成時間縮短 10.8
                秒，命名語意同樣是此系統介面的設計關鍵之一。
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
            <button
              onClick={() => router.push('/#work')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                border: 'none',
                padding: '0.9rem 2.25rem',
                borderRadius: '9999px',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 10px 25px rgba(255, 255, 255, 0.15)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeft size={18} />
              返回作品列表
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

const styles = {
  sectionHeading: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: '1.25rem',
    letterSpacing: '-0.02em',
    textAlign: 'center' as const,
  },
  paragraph: {
    fontSize: '1.05rem',
    color: '#334155',
    margin: 0,
    lineHeight: 1.7,
  },
  paragraphLarge: {
    fontSize: '1.15rem',
    color: '#334155',
    margin: 0,
    lineHeight: 1.8,
  },
  darkStatCard: {
    backgroundColor: 'rgba(30, 41, 59, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '2rem',
    backdropFilter: 'blur(10px)',
  },
  processCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '1.75rem',
  },
  processNum: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: '#4f46e5',
    backgroundColor: '#eef2ff',
    padding: '4px 12px',
    borderRadius: '8px',
    display: 'inline-block',
    marginBottom: '1rem',
  },
  processTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 0.5rem 0',
  },
  processDesc: {
    fontSize: '0.9rem',
    color: '#64748b',
    margin: 0,
    lineHeight: 1.5,
  },
  th: {
    padding: '1rem 1.25rem',
    fontWeight: 700,
    color: '#475569',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  td: {
    padding: '1rem 1.25rem',
    color: '#334155',
  },
  darkCard: {
    backgroundColor: '#0f172a',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '16px',
    padding: '2rem',
  },
  findingCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  badgeCritical: {
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#991b1b',
    backgroundColor: '#fee2e2',
    padding: '4px 12px',
    borderRadius: '99px',
    whiteSpace: 'nowrap' as const,
  },
  badgeModerate: {
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#92400e',
    backgroundColor: '#fef3c7',
    padding: '4px 12px',
    borderRadius: '99px',
  },
  badgeGood: {
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '4px 12px',
    borderRadius: '99px',
    whiteSpace: 'nowrap' as const,
  },
  evidenceCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #fde68a',
    borderRadius: '16px',
    padding: '1.75rem',
    boxShadow: '0 4px 16px rgba(120, 53, 15, 0.06)',
  },
  evidenceQuote: {
    margin: '0.9rem 0 0.9rem 0',
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#78350f',
    lineHeight: 1.7,
    fontStyle: 'italic' as const,
    borderLeft: '3px solid #fbbf24',
    paddingLeft: '1rem',
  },
  evidenceText: {
    margin: 0,
    fontSize: '0.92rem',
    color: '#57534e',
    lineHeight: 1.7,
  },
  chartCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '1.75rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  },
}
